/**
 * Elite Batch 18 Part 5 — Workflow Templates 1728–1747
 * Call-to-follow-up email generator, candidate sourcing from job boards,
 * careers page competitor hiring monitor, case study match for outbound,
 * case study mining pipeline, case study partnership request, case study viewer
 * trigger, challenge extraction from 10-K, champion blocker map, channel reseller
 * outreach, Clay direct email campaign, closed-lost partner recycle, closed-lost
 * recycle workflow, closed-won lookalike builder, co-marketing campaign pitch,
 * co-marketing webinar partner pitch, community moderator outreach, community
 * moderator recruiting, companies running paid search, companies running shopping ads.
 *
 * DESIGN RULES (inherited from prebuiltWorkflows.ts):
 * 1. Every variable MUST come from a real API response field or AI-generated output
 * 2. No magic variables — if a condition checks something, an upstream node must produce it
 * 3. Webhook triggers accept raw data — the FIRST node after a webhook trigger MUST be
 *    an AI extraction node that reads {webhook_payload} and outputs named variables.
 *    NEVER assume webhook payloads have specific field names (no {email}, {name}, etc.)
 * 4. Every condition has a REAL value (never blank, never "?")
 * 5. Services are pre-selected — user never sees "pick a service"
 * 6. Output variable names use the actual API response field paths
 * 7. Trigger inputFields should be EMPTY for webhooks — the AI extraction node declares outputs
 * 8. AI prompts for extraction should say "find the X regardless of format" to handle variations
 */

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}
function smsNode(
  id: string,
  label: string,
  y: number,
  to: string,
  message: string,
  x = 80,
): any {
  return n(id, "sendSMS", label, y, { sendSmsDetails: { to, message } }, x);
}
function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function hubspotGetNode(
  id: string,
  label: string,
  y: number,
  endpoint: string,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "GET",
        url: `https://api.hubapi.com/${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {hubspot_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [],
        variables: vars,
      },
    },
    x,
  );
}
function hubspotPostNode(
  id: string,
  label: string,
  y: number,
  endpoint: string,
  bodyFields: Array<{ key: string; value: string }>,
  outputs: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://api.hubapi.com/${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {hubspot_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: bodyFields,
        variables: outputs,
      },
    },
    x,
  );
}

// =============================================================================
// ELITE BATCH 18 PART 5 — TEMPLATES 1728–1747
// =============================================================================

export const ELITE_BATCH18_PART5: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ===================================================================
  // 1728. CALL-TO-FOLLOW-UP EMAIL GENERATOR (7 nodes)
  // ===================================================================

  "Call-to-Follow-Up Email Generator": {
    summary:
      "Fires when a sales call completes via webhook, retrieves the transcript from Gong API, AI-generates a call summary, AI-drafts a personalized follow-up email, attaches relevant resources with runCode, routes through rep review approval gate, and logs the activity to HubSpot CRM.",
    nodes: [
      trigger("Webhook: Sales call completed", "webhook_app", {
        source: "gong",
        event: "call.completed",
        howItStarts:
          "Fires when a sales call finishes in Gong, sending the raw call completion event with call ID, participants, duration, and metadata",
        setup:
          "Configure Gong webhook for call.completed events. Set Gong API key for transcript retrieval. Set OpenAI API key for AI summarization and email drafting. Configure HubSpot token for CRM activity logging.",
        inputFields: [],
      }),

      // 2. AI extract call metadata
      aiNode(
        "2",
        "AI Extract Call Metadata",
        280,
        "You are a sales call event parser. Extract the call ID, caller email, prospect email, prospect name, prospect company, call duration in minutes, call disposition, deal ID, deal stage, and call recording URL from the raw webhook payload. Respond JSON: {call_id, caller_email, prospect_email, prospect_name, prospect_company, call_duration_min, call_disposition, deal_id, deal_stage, recording_url}",
        "Raw webhook payload: {webhook_payload}. Extract all call completion data regardless of field naming conventions.",
        [
          { field: "call_id", as: "call_id", type: "string" },
          { field: "caller_email", as: "caller_email", type: "string" },
          { field: "prospect_email", as: "prospect_email", type: "string" },
          { field: "prospect_name", as: "prospect_name", type: "string" },
          { field: "prospect_company", as: "prospect_company", type: "string" },
          {
            field: "call_duration_min",
            as: "call_duration_min",
            type: "string",
          },
          { field: "call_disposition", as: "call_disposition", type: "string" },
          { field: "deal_id", as: "deal_id", type: "string" },
          { field: "deal_stage", as: "deal_stage", type: "string" },
          { field: "recording_url", as: "recording_url", type: "string" },
        ],
      ),

      // 3. Transcript retrieval Gong API
      n("3", "hitApi", "Retrieve Call Transcript from Gong", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.gong.io/v2/calls/{call_id}/transcript",
          headers: [
            { key: "Authorization", value: "Bearer {gong_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "transcript",
              customName: "call_transcript",
              type: "string",
            },
            {
              name: "duration",
              customName: "transcript_duration",
              type: "string",
            },
            { name: "speakers", customName: "speaker_list", type: "string" },
            { name: "topics", customName: "discussed_topics", type: "string" },
          ],
        },
      }),

      // 4. AI call summary generator
      aiNode(
        "4",
        "AI Generate Call Summary",
        760,
        "You are an expert sales call analyst. Given a call transcript, extract key discussion points, action items, objections raised, next steps agreed upon, competitor mentions, and buying signals. Create a concise summary for the sales rep and a separate prospect-facing recap. Return JSON: {summary, action_items, objections, next_steps, competitor_mentions, buying_signals, prospect_recap, follow_up_urgency}",
        "Transcript: {call_transcript} | Duration: {call_duration_min} min | Prospect: {prospect_name} at {prospect_company} | Deal Stage: {deal_stage} | Topics: {discussed_topics}",
        [
          { field: "summary", as: "call_summary", type: "string" },
          { field: "action_items", as: "action_items", type: "string" },
          { field: "objections", as: "objections_raised", type: "string" },
          { field: "next_steps", as: "agreed_next_steps", type: "string" },
          {
            field: "competitor_mentions",
            as: "competitor_mentions",
            type: "string",
          },
          { field: "buying_signals", as: "buying_signals", type: "string" },
          { field: "prospect_recap", as: "prospect_recap", type: "string" },
          {
            field: "follow_up_urgency",
            as: "follow_up_urgency",
            type: "string",
          },
        ],
      ),

      // 5. AI follow-up email drafter + resource attachment runCode
      n("5", "runCode", "Attach Relevant Resources to Follow-Up", 1000, {
        runCodeDetails: {
          code: `const objections = (variables.objections_raised || '').toLowerCase();
const stage = (variables.deal_stage || '').toLowerCase();
const signals = (variables.buying_signals || '').toLowerCase();
let resources = [];
if (objections.includes('price') || objections.includes('budget')) {
  resources.push('ROI Calculator: https://company.com/roi-calculator');
  resources.push('Pricing Comparison Guide: https://company.com/pricing-guide');
}
if (objections.includes('security') || objections.includes('compliance')) {
  resources.push('SOC 2 Report: https://company.com/security');
  resources.push('Compliance Overview: https://company.com/compliance');
}
if (objections.includes('integration') || objections.includes('technical')) {
  resources.push('Integration Docs: https://company.com/integrations');
  resources.push('API Reference: https://company.com/api-docs');
}
if (stage.includes('demo') || stage.includes('evaluation')) {
  resources.push('Product Tour Video: https://company.com/demo');
}
if (signals.includes('timeline') || signals.includes('urgent')) {
  resources.push('Quick Start Guide: https://company.com/quickstart');
}
if (resources.length === 0) {
  resources.push('Product Overview: https://company.com/overview');
  resources.push('Customer Stories: https://company.com/customers');
}
output.resource_links = resources.join('\\n');
output.resource_count = resources.length;
output.email_body = 'Hi ' + variables.prospect_name + ',\\n\\nThank you for taking the time to speak today. Here is a quick recap of our conversation:\\n\\n' + variables.prospect_recap + '\\n\\nAgreed Next Steps:\\n' + variables.agreed_next_steps + '\\n\\nAs promised, here are some resources that may be helpful:\\n' + resources.join('\\n') + '\\n\\nPlease do not hesitate to reach out if you have any questions.\\n\\nBest regards';
output.email_subject = 'Following up on our call — ' + variables.prospect_company + ' next steps';`,
          outputVariables: [
            { name: "resource_links", type: "string" },
            { name: "resource_count", type: "number" },
            { name: "email_body", type: "string" },
            { name: "email_subject", type: "string" },
          ],
        },
      }),

      // 6. Rep review approval gate
      n("6", "approvalGate", "Rep Review: Approve Follow-Up Email", 1240, {
        approvalGateDetails: {
          approvers: ["{caller_email}"],
          message:
            "Follow-up email ready for review after call with {prospect_name} ({prospect_company}).\\n\\nCall Summary: {call_summary}\\nAction Items: {action_items}\\nUrgency: {follow_up_urgency}\\n\\nSubject: {email_subject}\\n\\nEmail Preview:\\n{email_body}\\n\\nResources ({resource_count}): {resource_links}\\n\\nApprove to send to {prospect_email}.",
          timeout: 24,
        },
      }),

      // 7. CRM activity logging HubSpot
      hubspotPostNode(
        "7",
        "Log Call Activity to HubSpot CRM",
        1480,
        "crm/v3/objects/deals/{deal_id}",
        [
          {
            key: "properties",
            value:
              '{"notes_last_updated":"Call completed ({call_duration_min} min). Summary: {call_summary}. Action items: {action_items}. Next steps: {agreed_next_steps}. Follow-up email sent to {prospect_email}. Urgency: {follow_up_urgency}.","hs_next_step":"{agreed_next_steps}"}',
          },
        ],
        [
          { name: "id", customName: "updated_deal_id", type: "string" },
          {
            name: "properties.hs_next_step",
            customName: "logged_next_step",
            type: "string",
          },
        ],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1729. CANDIDATE SOURCING FROM JOB BOARDS (8 nodes)
  // ===================================================================

  "Candidate Sourcing from Job Boards": {
    summary:
      "Runs daily on a schedule, extracts candidate profiles from job board APIs, AI-analyzes skills fit, discovers contact information via enrichment API, validates emails, creates candidate records in Sheets, prioritizes recruiter queues via Slack, and sends automated outreach emails.",
    nodes: [
      trigger("Schedule: Daily job board candidate scan", "schedule", {
        schedule: "Every day at 7:00 AM",
        howItStarts:
          "Runs daily to scan job board APIs for new candidate profiles matching open roles, pulling in resume data, skills, experience, and location information",
        setup:
          "Configure job board API credentials (LinkedIn, Indeed, or Greenhouse). Set OpenAI API key for skills analysis. Configure email validation API key. Set Google Sheets credentials for candidate tracking. Configure Slack webhook for recruiter notifications.",
        inputFields: [],
      }),

      // 2. Profile extraction API
      n("2", "hitApi", "Extract Candidate Profiles from Job Board", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.greenhouse.io/v1/candidates?updated_after={last_run_date}&per_page=50",
          headers: [
            { key: "Authorization", value: "Basic {greenhouse_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "candidates[0].first_name",
              customName: "candidate_first_name",
              type: "string",
            },
            {
              name: "candidates[0].last_name",
              customName: "candidate_last_name",
              type: "string",
            },
            {
              name: "candidates[0].emails[0].value",
              customName: "candidate_email",
              type: "string",
            },
            {
              name: "candidates[0].title",
              customName: "candidate_title",
              type: "string",
            },
            {
              name: "candidates[0].company",
              customName: "candidate_company",
              type: "string",
            },
            {
              name: "candidates[0].applications[0].jobs[0].name",
              customName: "applied_role",
              type: "string",
            },
            {
              name: "candidates[0].tags",
              customName: "candidate_tags",
              type: "string",
            },
            { name: "total", customName: "total_candidates", type: "number" },
          ],
        },
      }),

      // 3. AI skills fit analysis
      aiNode(
        "3",
        "AI Analyze Skills Fit",
        520,
        "You are an expert technical recruiter. Evaluate the candidate profile against the target role. Assess technical skills match, experience level, cultural indicators, and overall fit. Score the candidate 0-100 and provide a hiring recommendation. Return JSON: {skills_match_score, experience_level, technical_skills_summary, culture_fit_indicators, recommendation, strengths, gaps, suggested_interview_focus, priority_tier}",
        "Candidate: {candidate_first_name} {candidate_last_name} | Title: {candidate_title} | Company: {candidate_company} | Applied Role: {applied_role} | Tags: {candidate_tags}",
        [
          {
            field: "skills_match_score",
            as: "skills_match_score",
            type: "string",
          },
          { field: "experience_level", as: "experience_level", type: "string" },
          {
            field: "technical_skills_summary",
            as: "tech_skills_summary",
            type: "string",
          },
          {
            field: "recommendation",
            as: "hiring_recommendation",
            type: "string",
          },
          { field: "strengths", as: "candidate_strengths", type: "string" },
          { field: "gaps", as: "candidate_gaps", type: "string" },
          {
            field: "suggested_interview_focus",
            as: "interview_focus",
            type: "string",
          },
          { field: "priority_tier", as: "priority_tier", type: "string" },
        ],
      ),

      // 4. Contact information discovery API
      n("4", "hitApi", "Discover Contact Information via Clearbit", 760, {
        hitApiDetails: {
          method: "GET",
          url: "https://person.clearbit.com/v2/people/find?email={candidate_email}",
          headers: [
            { key: "Authorization", value: "Bearer {clearbit_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "name.fullName",
              customName: "enriched_full_name",
              type: "string",
            },
            {
              name: "linkedin.handle",
              customName: "linkedin_handle",
              type: "string",
            },
            {
              name: "location",
              customName: "candidate_location",
              type: "string",
            },
            {
              name: "employment.name",
              customName: "current_employer",
              type: "string",
            },
            {
              name: "employment.title",
              customName: "current_title",
              type: "string",
            },
          ],
        },
      }),

      // 5. Email validation API
      n("5", "hitApi", "Validate Candidate Email Address", 1000, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.zerobounce.net/v2/validate?api_key={zerobounce_key}&email={candidate_email}",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [],
          variables: [
            { name: "status", customName: "email_status", type: "string" },
            {
              name: "sub_status",
              customName: "email_sub_status",
              type: "string",
            },
            { name: "free_email", customName: "is_free_email", type: "string" },
          ],
        },
      }),

      // 6. Candidate record creation Sheets
      sheetsNode(
        "6",
        "Create Candidate Record in Sheets",
        1240,
        "CandidatePipeline",
        `[["${new Date().toISOString().split("T")[0]}", "{candidate_first_name} {candidate_last_name}", "{candidate_email}", "{current_title}", "{current_employer}", "{applied_role}", "{skills_match_score}", "{priority_tier}", "{hiring_recommendation}", "{candidate_strengths}", "{candidate_gaps}", "{email_status}", "{linkedin_handle}", "{candidate_location}"]]`,
      ),

      // 7. Recruiter queue prioritization Slack
      slackNode(
        "7",
        "Notify Recruiter Queue via Slack",
        1480,
        "New candidate sourced for *{applied_role}*\\n*{candidate_first_name} {candidate_last_name}* — {current_title} at {current_employer}\\nSkills Match: {skills_match_score}/100 | Priority: {priority_tier}\\nRecommendation: {hiring_recommendation}\\nStrengths: {candidate_strengths}\\nGaps: {candidate_gaps}\\nInterview Focus: {interview_focus}\\nLinkedIn: {linkedin_handle} | Location: {candidate_location}\\nEmail Valid: {email_status}",
      ),

      // 8. Outreach automation email
      emailNode(
        "8",
        "Send Candidate Outreach Email",
        1720,
        "{candidate_email}",
        "Exciting opportunity at our company — {applied_role}",
        "Hi {candidate_first_name},\\n\\nI came across your profile and was impressed by your experience as {current_title} at {current_employer}.\\n\\nWe have an exciting opening for a {applied_role} position that seems like a great fit given your background in {tech_skills_summary}.\\n\\nA few highlights about the role and our team:\\n- Competitive compensation and equity package\\n- Flexible remote-first culture\\n- Cutting-edge technical challenges\\n\\nWould you be open to a brief conversation this week to learn more?\\n\\nBest regards,\\nTalent Team",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1730. CAREERS PAGE COMPETITOR HIRING MONITOR (8 nodes)
  // ===================================================================

  "Careers Page Competitor Hiring Monitor": {
    summary:
      "Runs weekly to scrape competitor careers pages via API, compares against historical data with runCode, AI-analyzes hiring patterns, AI-generates sales intelligence, distributes competitive alerts via Slack, enriches CRM accounts in HubSpot, and conditionally triggers opportunity detection based on hiring velocity.",
    nodes: [
      trigger("Schedule: Weekly competitor careers page scan", "schedule", {
        schedule: "Every Monday at 8:00 AM",
        howItStarts:
          "Runs weekly to scan competitor careers pages for new job postings, role changes, and hiring velocity patterns that indicate market moves, expansion, or technology shifts",
        setup:
          "Configure web scraping API credentials (ScrapingBee or Apify). Set OpenAI API key for pattern analysis and intelligence generation. Configure Slack webhook for competitive alerts. Set HubSpot token for account enrichment.",
        inputFields: [],
      }),

      // 2. Careers page scraping API
      n("2", "hitApi", "Scrape Competitor Careers Pages via API", 280, {
        hitApiDetails: {
          method: "POST",
          url: "https://app.scrapingbee.com/api/v1/",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [
            { key: "api_key", value: "{scrapingbee_key}" },
            { key: "url", value: "{competitor_careers_url}" },
            {
              key: "extract_rules",
              value:
                '{"job_titles":"h3.job-title","departments":".department","locations":".location","posting_dates":".post-date"}',
            },
          ],
          variables: [
            {
              name: "body.job_titles",
              customName: "current_job_titles",
              type: "string",
            },
            {
              name: "body.departments",
              customName: "current_departments",
              type: "string",
            },
            {
              name: "body.locations",
              customName: "current_locations",
              type: "string",
            },
            {
              name: "body.posting_dates",
              customName: "current_posting_dates",
              type: "string",
            },
            {
              name: "status-code",
              customName: "scrape_status",
              type: "number",
            },
          ],
        },
      }),

      // 3. Historical comparison runCode
      n("3", "runCode", "Compare Against Historical Job Data", 520, {
        runCodeDetails: {
          code: `const current = (variables.current_job_titles || '').split(',').map(t => t.trim()).filter(Boolean);
const currentDepts = (variables.current_departments || '').split(',').map(d => d.trim()).filter(Boolean);
const totalOpenRoles = current.length;
const engineeringRoles = current.filter(t => t.toLowerCase().match(/engineer|developer|sre|devops|architect/)).length;
const salesRoles = current.filter(t => t.toLowerCase().match(/sales|account|bdm|sdr|ae/)).length;
const productRoles = current.filter(t => t.toLowerCase().match(/product|pm|designer|ux/)).length;
const leadershipRoles = current.filter(t => t.toLowerCase().match(/vp|director|head|chief|lead/)).length;
const hiringVelocity = totalOpenRoles > 20 ? 'aggressive' : totalOpenRoles > 10 ? 'moderate' : 'conservative';
const dominantDept = currentDepts.reduce((acc, d) => { acc[d] = (acc[d] || 0) + 1; return acc; }, {});
const topDept = Object.entries(dominantDept).sort((a, b) => b[1] - a[1])[0];
output.total_open_roles = totalOpenRoles;
output.engineering_count = engineeringRoles;
output.sales_count = salesRoles;
output.product_count = productRoles;
output.leadership_count = leadershipRoles;
output.hiring_velocity = hiringVelocity;
output.top_department = topDept ? topDept[0] : 'unknown';
output.role_breakdown = 'Eng:' + engineeringRoles + ' Sales:' + salesRoles + ' Product:' + productRoles + ' Leadership:' + leadershipRoles;`,
          outputVariables: [
            { name: "total_open_roles", type: "number" },
            { name: "engineering_count", type: "number" },
            { name: "sales_count", type: "number" },
            { name: "product_count", type: "number" },
            { name: "leadership_count", type: "number" },
            { name: "hiring_velocity", type: "string" },
            { name: "top_department", type: "string" },
            { name: "role_breakdown", type: "string" },
          ],
        },
      }),

      // 4. AI hiring pattern analysis
      aiNode(
        "4",
        "AI Analyze Hiring Patterns",
        760,
        "You are a competitive intelligence analyst. Analyze the competitor hiring data to identify strategic moves, expansion areas, technology bets, and market signals. Look for patterns indicating new product launches, market entry, or organizational restructuring. Return JSON: {strategic_signal, expansion_areas, technology_bets, market_implications, threat_level, opportunity_angle, recommended_response, confidence_score}",
        "Competitor Jobs: {current_job_titles} | Departments: {current_departments} | Locations: {current_locations} | Total Open: {total_open_roles} | Velocity: {hiring_velocity} | Breakdown: {role_breakdown} | Top Dept: {top_department}",
        [
          { field: "strategic_signal", as: "strategic_signal", type: "string" },
          { field: "expansion_areas", as: "expansion_areas", type: "string" },
          { field: "technology_bets", as: "technology_bets", type: "string" },
          {
            field: "market_implications",
            as: "market_implications",
            type: "string",
          },
          { field: "threat_level", as: "threat_level", type: "string" },
          {
            field: "opportunity_angle",
            as: "opportunity_angle",
            type: "string",
          },
          {
            field: "recommended_response",
            as: "recommended_response",
            type: "string",
          },
          { field: "confidence_score", as: "confidence_score", type: "string" },
        ],
      ),

      // 5. AI sales intelligence generation
      aiNode(
        "5",
        "AI Generate Sales Intelligence Brief",
        1000,
        "You are a sales intelligence strategist. Based on the competitor hiring analysis, generate actionable intelligence for the sales team. Identify prospects who may be affected by competitor moves, craft displacement messaging, and suggest timing for outreach. Return JSON: {intelligence_brief, displacement_messaging, target_personas, outreach_timing, talk_track, battle_card_update, competitive_positioning}",
        "Signal: {strategic_signal} | Expansion: {expansion_areas} | Tech Bets: {technology_bets} | Threat: {threat_level} | Opportunity: {opportunity_angle} | Response: {recommended_response} | Velocity: {hiring_velocity} | Open Roles: {total_open_roles}",
        [
          {
            field: "intelligence_brief",
            as: "intelligence_brief",
            type: "string",
          },
          {
            field: "displacement_messaging",
            as: "displacement_messaging",
            type: "string",
          },
          { field: "target_personas", as: "target_personas", type: "string" },
          { field: "outreach_timing", as: "outreach_timing", type: "string" },
          { field: "talk_track", as: "talk_track", type: "string" },
          {
            field: "battle_card_update",
            as: "battle_card_update",
            type: "string",
          },
          {
            field: "competitive_positioning",
            as: "competitive_positioning",
            type: "string",
          },
        ],
      ),

      // 6. Competitive alert distribution Slack
      slackNode(
        "6",
        "Distribute Competitive Alert via Slack",
        1240,
        "Competitor Hiring Intelligence Update\\n\\nTotal Open Roles: {total_open_roles} | Velocity: {hiring_velocity}\\nBreakdown: {role_breakdown}\\nTop Hiring Dept: {top_department}\\n\\nStrategic Signal: {strategic_signal}\\nThreat Level: {threat_level}\\nExpansion Areas: {expansion_areas}\\nTech Bets: {technology_bets}\\n\\nSales Intelligence:\\n{intelligence_brief}\\n\\nDisplacement Messaging: {displacement_messaging}\\nTarget Personas: {target_personas}\\nOutreach Timing: {outreach_timing}",
      ),

      // 7. CRM enrichment HubSpot
      hubspotPostNode(
        "7",
        "Enrich Competitor Account in HubSpot",
        1480,
        "crm/v3/objects/companies/search",
        [
          {
            key: "properties",
            value:
              '{"notes_last_updated":"Competitor hiring update: {total_open_roles} open roles. Velocity: {hiring_velocity}. Signal: {strategic_signal}. Threat: {threat_level}. Battle card update: {battle_card_update}."}',
          },
        ],
        [{ name: "id", customName: "updated_company_id", type: "string" }],
      ),

      // 8. Opportunity trigger detection condition
      n(
        "8",
        "condition",
        "Check if hiring velocity indicates opportunity",
        1720,
        {
          conditionDetails: {
            conditions: [
              {
                variable: "hiring_velocity",
                operator: "equals",
                value: "aggressive",
              },
            ],
            logicalOperator: "AND",
          },
        },
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1731. CASE STUDY MATCH FOR OUTBOUND (7 nodes)
  // ===================================================================

  "Case Study Match for Outbound": {
    summary:
      "Fires when a new outbound prospect is added via webhook, extracts prospect profile from HubSpot, indexes the case study library via API, AI-matches the most relevant case study semantically, AI-generates personalized context, integrates into the sales sequence API, and tracks match effectiveness in Sheets.",
    nodes: [
      trigger("Webhook: New outbound prospect added", "webhook_app", {
        source: "hubspot",
        event: "contact.created",
        howItStarts:
          "Fires when a new outbound prospect is created in the CRM, triggering case study matching to enrich the prospecting sequence with relevant social proof",
        setup:
          "Configure HubSpot webhook for contact.created events filtered to outbound source. Set HubSpot API token for prospect data. Configure case study CMS API. Set OpenAI API key for semantic matching. Configure sequence integration API.",
        inputFields: [],
      }),

      // 2. Prospect profile extraction HubSpot
      aiNode(
        "2",
        "AI Extract Prospect Details",
        280,
        "You are a CRM data parser. Extract the prospect email, name, company, job title, industry, company size, deal stage, lead source, and any custom properties from the raw webhook payload. Respond JSON: {prospect_email, prospect_name, prospect_company, prospect_title, prospect_industry, company_size, deal_stage, lead_source, prospect_id}",
        "Raw webhook payload: {webhook_payload}. Extract all prospect data regardless of field naming.",
        [
          { field: "prospect_email", as: "prospect_email", type: "string" },
          { field: "prospect_name", as: "prospect_name", type: "string" },
          { field: "prospect_company", as: "prospect_company", type: "string" },
          { field: "prospect_title", as: "prospect_title", type: "string" },
          {
            field: "prospect_industry",
            as: "prospect_industry",
            type: "string",
          },
          { field: "company_size", as: "company_size", type: "string" },
          { field: "lead_source", as: "lead_source", type: "string" },
          { field: "prospect_id", as: "prospect_id", type: "string" },
        ],
      ),

      // 3. Case study library index API
      n("3", "hitApi", "Fetch Case Study Library Index", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.contentful.com/spaces/{contentful_space_id}/entries?content_type=caseStudy&select=fields.title,fields.industry,fields.companySize,fields.metrics,fields.tags,fields.slug",
          headers: [
            { key: "Authorization", value: "Bearer {contentful_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "items", customName: "case_study_catalog", type: "string" },
            { name: "total", customName: "total_case_studies", type: "number" },
          ],
        },
      }),

      // 4. AI semantic matching
      aiNode(
        "4",
        "AI Match Best Case Study Semantically",
        760,
        "You are a sales content strategist. Given a prospect profile and a catalog of case studies, find the best matching case study based on industry alignment, company size similarity, role relevance, and pain point overlap. Score each match and explain why it resonates. Return JSON: {matched_title, matched_slug, match_score, industry_alignment, size_similarity, role_relevance, key_metric, resonance_reason, alternative_title, alternative_reason}",
        "Prospect: {prospect_name} at {prospect_company} | Title: {prospect_title} | Industry: {prospect_industry} | Size: {company_size} | Case Studies Available: {case_study_catalog}",
        [
          { field: "matched_title", as: "matched_case_study", type: "string" },
          { field: "matched_slug", as: "case_study_slug", type: "string" },
          { field: "match_score", as: "match_score", type: "string" },
          {
            field: "industry_alignment",
            as: "industry_alignment",
            type: "string",
          },
          { field: "key_metric", as: "key_metric", type: "string" },
          { field: "resonance_reason", as: "resonance_reason", type: "string" },
          { field: "alternative_title", as: "alt_case_study", type: "string" },
          { field: "alternative_reason", as: "alt_reason", type: "string" },
        ],
      ),

      // 5. AI personalized context generator
      aiNode(
        "5",
        "AI Generate Personalized Outreach Context",
        1000,
        "You are an expert SDR copywriter. Given the matched case study and prospect profile, craft a personalized email snippet that naturally weaves in the case study as social proof. Do not be salesy — be consultative and genuine. Return JSON: {email_snippet, subject_line_variant, case_study_intro, metric_highlight, call_to_action, personalization_hook}",
        "Prospect: {prospect_name}, {prospect_title} at {prospect_company} ({prospect_industry}, {company_size} employees) | Matched Case Study: {matched_case_study} | Key Metric: {key_metric} | Why it resonates: {resonance_reason} | Match Score: {match_score}",
        [
          { field: "email_snippet", as: "email_snippet", type: "string" },
          {
            field: "subject_line_variant",
            as: "subject_variant",
            type: "string",
          },
          { field: "case_study_intro", as: "case_study_intro", type: "string" },
          { field: "metric_highlight", as: "metric_highlight", type: "string" },
          { field: "call_to_action", as: "cta_text", type: "string" },
          {
            field: "personalization_hook",
            as: "personalization_hook",
            type: "string",
          },
        ],
      ),

      // 6. Sequence integration API
      n("6", "hitApi", "Inject Case Study into Sales Sequence", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.hubapi.com/crm/v3/objects/contacts/{prospect_id}",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "properties",
              value:
                '{"hs_lead_status":"IN_SEQUENCE","notes_last_updated":"Case study matched: {matched_case_study} (score: {match_score}). Hook: {personalization_hook}. CTA: {cta_text}. Alt: {alt_case_study}."}',
            },
          ],
          variables: [
            { name: "id", customName: "updated_contact_id", type: "string" },
            {
              name: "properties.hs_lead_status",
              customName: "sequence_status",
              type: "string",
            },
          ],
        },
      }),

      // 7. Match effectiveness tracking Sheets
      sheetsNode(
        "7",
        "Track Case Study Match Effectiveness",
        1480,
        "CaseStudyMatches",
        `[["${new Date().toISOString().split("T")[0]}", "{prospect_name}", "{prospect_company}", "{prospect_industry}", "{company_size}", "{matched_case_study}", "{match_score}", "{key_metric}", "{resonance_reason}", "{personalization_hook}", "{alt_case_study}", "{lead_source}"]]`,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1732. CASE STUDY MINING PIPELINE (9 nodes)
  // ===================================================================

  "Case Study Mining Pipeline": {
    summary:
      "Runs monthly to extract success metrics from HubSpot, validates customer health via API, AI-generates story angles, scores candidates with runCode, routes through CSM pre-qualification approval gate, sends customer outreach email, AI-drafts case study content, and logs publishing candidates to Sheets.",
    nodes: [
      trigger("Schedule: Monthly case study candidate scan", "schedule", {
        schedule: "First Monday of every month at 10:00 AM",
        howItStarts:
          "Runs monthly to identify customers with strong success metrics who would make compelling case study candidates, systematically mining the customer base for storytelling opportunities",
        setup:
          "Configure HubSpot API token for customer data extraction. Set customer health platform API key. Set OpenAI API key for story generation. Configure email sending credentials. Set Google Sheets for publishing pipeline tracking.",
        inputFields: [],
      }),

      // 2. Success metric extraction HubSpot
      hubspotGetNode(
        "2",
        "Extract Customer Success Metrics from HubSpot",
        280,
        'crm/v3/objects/deals/search?filterGroups=[{"filters":[{"propertyName":"dealstage","operator":"EQ","value":"closedwon"},{"propertyName":"closedate","operator":"GTE","value":"{six_months_ago}"}]}]&properties=dealname,amount,closedate,hs_deal_stage_probability,pipeline,num_notes',
        [
          { name: "results[0].id", customName: "deal_id", type: "string" },
          {
            name: "results[0].properties.dealname",
            customName: "customer_name",
            type: "string",
          },
          {
            name: "results[0].properties.amount",
            customName: "deal_amount",
            type: "string",
          },
          {
            name: "results[0].properties.closedate",
            customName: "close_date",
            type: "string",
          },
          {
            name: "results[0].properties.pipeline",
            customName: "deal_pipeline",
            type: "string",
          },
          { name: "total", customName: "total_candidates", type: "number" },
        ],
      ),

      // 3. Customer health validation API
      n("3", "hitApi", "Validate Customer Health Score", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.gainsight.com/v1/data/objects/Company?select=Name,HealthScore,NPS,MRR,UsageScore,RenewalDate&where=Name eq '{customer_name}'",
          headers: [
            { key: "Authorization", value: "Bearer {gainsight_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "data[0].HealthScore",
              customName: "health_score",
              type: "string",
            },
            { name: "data[0].NPS", customName: "nps_score", type: "string" },
            { name: "data[0].MRR", customName: "customer_mrr", type: "string" },
            {
              name: "data[0].UsageScore",
              customName: "usage_score",
              type: "string",
            },
            {
              name: "data[0].RenewalDate",
              customName: "renewal_date",
              type: "string",
            },
          ],
        },
      }),

      // 4. AI story angle generator
      aiNode(
        "4",
        "AI Generate Case Study Story Angles",
        760,
        "You are a content marketing strategist specializing in B2B case studies. Given a customer's success metrics, generate 3 compelling story angles that would resonate with different buyer personas. Each angle should have a headline, key narrative, target audience, and estimated impact. Return JSON: {angle_1_headline, angle_1_narrative, angle_1_audience, angle_2_headline, angle_2_narrative, angle_2_audience, angle_3_headline, angle_3_narrative, angle_3_audience, recommended_angle, story_strength_score}",
        "Customer: {customer_name} | Deal: ${deal_amount} | Health: {health_score} | NPS: {nps_score} | MRR: ${customer_mrr} | Usage: {usage_score} | Pipeline: {deal_pipeline} | Renewal: {renewal_date}",
        [
          { field: "angle_1_headline", as: "angle_1_headline", type: "string" },
          {
            field: "angle_1_narrative",
            as: "angle_1_narrative",
            type: "string",
          },
          { field: "angle_1_audience", as: "angle_1_audience", type: "string" },
          { field: "angle_2_headline", as: "angle_2_headline", type: "string" },
          {
            field: "angle_2_narrative",
            as: "angle_2_narrative",
            type: "string",
          },
          {
            field: "recommended_angle",
            as: "recommended_angle",
            type: "string",
          },
          {
            field: "story_strength_score",
            as: "story_strength_score",
            type: "string",
          },
        ],
      ),

      // 5. Candidate scoring prioritization runCode
      n("5", "runCode", "Score and Prioritize Case Study Candidate", 1000, {
        runCodeDetails: {
          code: `const health = parseInt(variables.health_score) || 0;
const nps = parseInt(variables.nps_score) || 0;
const usage = parseInt(variables.usage_score) || 0;
const dealAmount = parseInt(variables.deal_amount) || 0;
const storyStrength = parseInt(variables.story_strength_score) || 0;
let score = 0;
score += health >= 80 ? 30 : health >= 60 ? 20 : 10;
score += nps >= 9 ? 25 : nps >= 7 ? 15 : 5;
score += usage >= 80 ? 20 : usage >= 50 ? 10 : 5;
score += dealAmount >= 50000 ? 15 : dealAmount >= 10000 ? 10 : 5;
score += storyStrength >= 8 ? 10 : storyStrength >= 5 ? 5 : 2;
const tier = score >= 80 ? 'platinum' : score >= 60 ? 'gold' : score >= 40 ? 'silver' : 'bronze';
const csm_approval_needed = score >= 50 ? 'yes' : 'no';
output.candidate_score = score;
output.candidate_tier = tier;
output.csm_approval_needed = csm_approval_needed;
output.scoring_breakdown = 'Health:' + health + ' NPS:' + nps + ' Usage:' + usage + ' Deal:' + dealAmount + ' Story:' + storyStrength;`,
          outputVariables: [
            { name: "candidate_score", type: "number" },
            { name: "candidate_tier", type: "string" },
            { name: "csm_approval_needed", type: "string" },
            { name: "scoring_breakdown", type: "string" },
          ],
        },
      }),

      // 6. CSM pre-qualification approval gate
      n("6", "approvalGate", "CSM Pre-Qualification Approval", 1240, {
        approvalGateDetails: {
          approvers: ["{csm_team_email}"],
          message:
            "Case study candidate identified for review:\\n\\nCustomer: {customer_name} | Deal: ${deal_amount}\\nHealth Score: {health_score} | NPS: {nps_score} | Usage: {usage_score}\\nCandidate Score: {candidate_score} ({candidate_tier} tier)\\nScoring: {scoring_breakdown}\\n\\nRecommended Angle: {recommended_angle}\\nHeadline: {angle_1_headline}\\nNarrative: {angle_1_narrative}\\nAudience: {angle_1_audience}\\n\\nApprove to proceed with customer outreach.",
          timeout: 72,
        },
      }),

      // 7. Customer outreach email
      emailNode(
        "7",
        "Send Case Study Participation Request",
        1480,
        "{customer_name}",
        "We would love to share your success story, {customer_name}",
        "Hi {customer_name},\\n\\nWe have been incredibly impressed with what your team has achieved since partnering with us.\\n\\nYour results speak for themselves — Health Score: {health_score}, NPS: {nps_score}, and exceptional product adoption.\\n\\nWe would love to feature your story in a case study: {angle_1_headline}\\n\\nThe process is straightforward:\\n- A 30-minute interview with your team\\n- We draft everything — you just review and approve\\n- You get early access to the published piece for your own marketing\\n\\nWould you be open to participating? We are happy to work around your schedule.\\n\\nBest regards,\\nContent Team",
      ),

      // 8. AI case study draft generation
      aiNode(
        "8",
        "AI Draft Case Study Content",
        1720,
        "You are an expert B2B case study writer. Based on the customer data and recommended angle, draft an initial case study outline with sections: Challenge, Solution, Results, and Quote suggestions. Make it compelling and metric-driven. Return JSON: {title, challenge_section, solution_section, results_section, suggested_quotes, seo_keywords, estimated_word_count}",
        "Customer: {customer_name} | Angle: {recommended_angle} | Headline: {angle_1_headline} | Narrative: {angle_1_narrative} | Deal: ${deal_amount} | Health: {health_score} | NPS: {nps_score} | Usage: {usage_score}",
        [
          { field: "title", as: "draft_title", type: "string" },
          { field: "challenge_section", as: "draft_challenge", type: "string" },
          { field: "solution_section", as: "draft_solution", type: "string" },
          { field: "results_section", as: "draft_results", type: "string" },
          { field: "suggested_quotes", as: "draft_quotes", type: "string" },
          { field: "seo_keywords", as: "seo_keywords", type: "string" },
        ],
      ),

      // 9. Publishing distribution Sheets
      sheetsNode(
        "9",
        "Log Case Study to Publishing Pipeline",
        1960,
        "CaseStudyPipeline",
        `[["${new Date().toISOString().split("T")[0]}", "{customer_name}", "{deal_amount}", "{candidate_score}", "{candidate_tier}", "{recommended_angle}", "{draft_title}", "{health_score}", "{nps_score}", "{usage_score}", "outreach_sent", "{seo_keywords}"]]`,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 1733. CASE STUDY PARTNERSHIP REQUEST (7 nodes)
  // ===================================================================

  "Case Study Partnership Request": {
    summary:
      "Fires when a partnership request arrives via webhook, validates customer success history in HubSpot, AI-generates a personalized partnership request, coordinates with the CSM via Slack, sends the customer outreach email, branches on response tracking condition, and kicks off the case study project in Asana.",
    nodes: [
      trigger(
        "Webhook: Case study partnership request submitted",
        "webhook_app",
        {
          source: "internal",
          event: "case_study.partnership_requested",
          howItStarts:
            "Fires when a sales or marketing team member submits a case study partnership request for a specific customer, indicating the customer is ready for a co-branded content opportunity",
          setup:
            "Configure internal webhook for partnership requests. Set HubSpot API token for customer validation. Set OpenAI API key for personalized request generation. Configure Slack webhook for CSM coordination. Set Asana API key for project creation.",
          inputFields: [],
        },
      ),

      // 2. AI extract request data
      aiNode(
        "2",
        "AI Extract Partnership Request Data",
        280,
        "You are a partnership request parser. Extract the customer name, customer email, customer contact name, requesting team member email, partnership type, proposed topic, urgency level, customer account ID, and any special notes from the raw webhook payload. Respond JSON: {customer_name, customer_email, contact_name, requester_email, partnership_type, proposed_topic, urgency, account_id, special_notes}",
        "Raw webhook payload: {webhook_payload}. Extract all partnership request data regardless of field naming conventions.",
        [
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "contact_name", as: "contact_name", type: "string" },
          { field: "requester_email", as: "requester_email", type: "string" },
          { field: "partnership_type", as: "partnership_type", type: "string" },
          { field: "proposed_topic", as: "proposed_topic", type: "string" },
          { field: "urgency", as: "request_urgency", type: "string" },
          { field: "account_id", as: "account_id", type: "string" },
          { field: "special_notes", as: "special_notes", type: "string" },
        ],
      ),

      // 3. Customer success validation HubSpot
      hubspotGetNode(
        "3",
        "Validate Customer Success History in HubSpot",
        520,
        "crm/v3/objects/companies/{account_id}?properties=name,industry,numberofemployees,hs_lead_status,notes_last_contacted,num_associated_deals,lifecyclestage,hs_analytics_num_page_views",
        [
          {
            name: "properties.name",
            customName: "hs_company_name",
            type: "string",
          },
          {
            name: "properties.industry",
            customName: "hs_industry",
            type: "string",
          },
          {
            name: "properties.numberofemployees",
            customName: "hs_employee_count",
            type: "string",
          },
          {
            name: "properties.hs_lead_status",
            customName: "hs_lead_status",
            type: "string",
          },
          {
            name: "properties.num_associated_deals",
            customName: "total_deals",
            type: "string",
          },
          {
            name: "properties.lifecyclestage",
            customName: "lifecycle_stage",
            type: "string",
          },
        ],
      ),

      // 4. AI personalized request generation
      aiNode(
        "4",
        "AI Generate Personalized Partnership Request",
        760,
        "You are a partnership communications expert. Craft a warm, personalized case study partnership request email for the customer contact. Reference their success, explain the mutual benefits, and make the ask feel like an honor rather than a request. Return JSON: {email_subject, email_body, value_proposition, mutual_benefits, time_commitment, incentive_offer, follow_up_cadence}",
        "Customer: {hs_company_name} ({hs_industry}, {hs_employee_count} employees) | Contact: {contact_name} | Status: {hs_lead_status} | Deals: {total_deals} | Partnership Type: {partnership_type} | Topic: {proposed_topic} | Notes: {special_notes}",
        [
          { field: "email_subject", as: "partnership_subject", type: "string" },
          { field: "email_body", as: "partnership_body", type: "string" },
          { field: "value_proposition", as: "value_prop", type: "string" },
          { field: "mutual_benefits", as: "mutual_benefits", type: "string" },
          { field: "time_commitment", as: "time_commitment", type: "string" },
          { field: "incentive_offer", as: "incentive_offer", type: "string" },
          {
            field: "follow_up_cadence",
            as: "follow_up_cadence",
            type: "string",
          },
        ],
      ),

      // 5. CSM coordination Slack
      slackNode(
        "5",
        "Coordinate with CSM via Slack",
        1000,
        "Case Study Partnership Request\\n\\nCustomer: *{hs_company_name}* ({hs_industry})\\nContact: {contact_name} ({customer_email})\\nRequested by: {requester_email}\\nPartnership Type: {partnership_type}\\nTopic: {proposed_topic}\\nUrgency: {request_urgency}\\n\\nValue Prop: {value_prop}\\nTime Commitment: {time_commitment}\\nIncentive: {incentive_offer}\\n\\nPartnership email will be sent to the customer. Please flag any concerns immediately.",
      ),

      // 6. Customer outreach email
      emailNode(
        "6",
        "Send Partnership Request to Customer",
        1240,
        "{customer_email}",
        "{partnership_subject}",
        "{partnership_body}",
      ),

      // 7. Response tracking condition
      n(
        "7",
        "condition",
        "Check if request urgency warrants project kickoff",
        1480,
        {
          conditionDetails: {
            conditions: [
              {
                variable: "request_urgency",
                operator: "equals",
                value: "high",
              },
            ],
            logicalOperator: "AND",
          },
        },
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1734. CASE STUDY VIEWER TRIGGER (8 nodes)
  // ===================================================================

  "Case Study Viewer Trigger": {
    summary:
      "Fires when a prospect views a case study page via webhook, enriches the viewer identity via Clearbit API, analyzes content relevance with runCode, AI-maps relevance to prospect needs, updates engagement scoring in HubSpot, sends a targeted follow-up email, alerts the sales rep via Slack, and checks for deal acceleration opportunity.",
    nodes: [
      trigger("Webhook: Case study page viewed", "webhook_app", {
        source: "website_analytics",
        event: "page.viewed.case_study",
        howItStarts:
          "Fires when a known or identified visitor views a case study page on the website, sending the raw page view event with visitor data, page URL, and session information",
        setup:
          "Configure website analytics webhook for case study page views. Set Clearbit API key for visitor enrichment. Set OpenAI API key for relevance mapping. Configure HubSpot token for engagement scoring. Configure email credentials and Slack webhook.",
        inputFields: [],
      }),

      // 2. AI extract viewer data
      aiNode(
        "2",
        "AI Extract Case Study Viewer Data",
        280,
        "You are a web analytics event parser. Extract the viewer email, viewer name, page URL, case study title, page time spent in seconds, referrer URL, UTM parameters, session ID, device type, and any form data from the raw webhook payload. Respond JSON: {viewer_email, viewer_name, page_url, case_study_title, time_on_page_sec, referrer_url, utm_source, utm_campaign, session_id, device_type}",
        "Raw webhook payload: {webhook_payload}. Extract all case study page view data regardless of field naming conventions.",
        [
          { field: "viewer_email", as: "viewer_email", type: "string" },
          { field: "viewer_name", as: "viewer_name", type: "string" },
          { field: "page_url", as: "page_url", type: "string" },
          { field: "case_study_title", as: "case_study_title", type: "string" },
          { field: "time_on_page_sec", as: "time_on_page", type: "string" },
          { field: "referrer_url", as: "referrer_url", type: "string" },
          { field: "utm_source", as: "utm_source", type: "string" },
          { field: "utm_campaign", as: "utm_campaign", type: "string" },
          { field: "session_id", as: "session_id", type: "string" },
          { field: "device_type", as: "device_type", type: "string" },
        ],
      ),

      // 3. Viewer identification enrichment Clearbit API
      n("3", "hitApi", "Enrich Viewer Identity via Clearbit", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://person.clearbit.com/v2/people/find?email={viewer_email}",
          headers: [
            { key: "Authorization", value: "Bearer {clearbit_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "name.fullName",
              customName: "enriched_name",
              type: "string",
            },
            { name: "title", customName: "enriched_title", type: "string" },
            { name: "role", customName: "enriched_role", type: "string" },
            {
              name: "seniority",
              customName: "enriched_seniority",
              type: "string",
            },
            {
              name: "company.name",
              customName: "enriched_company",
              type: "string",
            },
            {
              name: "company.category.industry",
              customName: "viewer_industry",
              type: "string",
            },
            {
              name: "company.metrics.employees",
              customName: "viewer_company_size",
              type: "string",
            },
          ],
        },
      }),

      // 4. Case study content analysis runCode
      n("4", "runCode", "Analyze Case Study Content Relevance", 760, {
        runCodeDetails: {
          code: `const timeOnPage = parseInt(variables.time_on_page) || 0;
const title = (variables.case_study_title || '').toLowerCase();
const viewerIndustry = (variables.viewer_industry || '').toLowerCase();
const seniority = (variables.enriched_seniority || '').toLowerCase();
let engagement_level = 'low';
if (timeOnPage > 180) engagement_level = 'high';
else if (timeOnPage > 60) engagement_level = 'medium';
const industryMatch = title.includes(viewerIndustry) || viewerIndustry.includes('technology') || viewerIndustry.includes('software');
const isDecisionMaker = seniority === 'executive' || seniority === 'director' || seniority === 'vp';
let intent_score = 0;
intent_score += timeOnPage > 120 ? 40 : timeOnPage > 60 ? 25 : 10;
intent_score += industryMatch ? 25 : 10;
intent_score += isDecisionMaker ? 25 : 10;
intent_score += variables.utm_source ? 10 : 0;
output.engagement_level = engagement_level;
output.industry_match = industryMatch ? 'yes' : 'no';
output.is_decision_maker = isDecisionMaker ? 'yes' : 'no';
output.intent_score = Math.min(100, intent_score);
output.follow_up_priority = intent_score >= 70 ? 'high' : intent_score >= 40 ? 'medium' : 'low';`,
          outputVariables: [
            { name: "engagement_level", type: "string" },
            { name: "industry_match", type: "string" },
            { name: "is_decision_maker", type: "string" },
            { name: "intent_score", type: "number" },
            { name: "follow_up_priority", type: "string" },
          ],
        },
      }),

      // 5. AI relevance mapping
      aiNode(
        "5",
        "AI Map Relevance to Prospect Needs",
        1000,
        "You are a sales intelligence analyst. Given a case study viewer's profile and their engagement data, infer what they are researching, what pain points the case study addresses for them, and craft a hyper-relevant follow-up message. Return JSON: {inferred_pain_point, research_stage, follow_up_subject, follow_up_message, recommended_content, next_case_study_recommendation}",
        "Viewer: {enriched_name} ({enriched_title}) at {enriched_company} ({viewer_industry}, {viewer_company_size} employees) | Seniority: {enriched_seniority} | Case Study: {case_study_title} | Engagement: {engagement_level} | Time: {time_on_page}s | Intent Score: {intent_score} | Industry Match: {industry_match} | Source: {utm_source} | Campaign: {utm_campaign}",
        [
          {
            field: "inferred_pain_point",
            as: "inferred_pain_point",
            type: "string",
          },
          { field: "research_stage", as: "research_stage", type: "string" },
          {
            field: "follow_up_subject",
            as: "follow_up_subject",
            type: "string",
          },
          {
            field: "follow_up_message",
            as: "follow_up_message",
            type: "string",
          },
          {
            field: "recommended_content",
            as: "recommended_content",
            type: "string",
          },
          {
            field: "next_case_study_recommendation",
            as: "next_case_study",
            type: "string",
          },
        ],
      ),

      // 6. Engagement scoring update HubSpot
      hubspotPostNode(
        "6",
        "Update Engagement Score in HubSpot",
        1240,
        "crm/v3/objects/contacts/search",
        [
          {
            key: "filterGroups",
            value:
              '[{"filters":[{"propertyName":"email","operator":"EQ","value":"{viewer_email}"}]}]',
          },
          {
            key: "properties",
            value:
              '{"notes_last_updated":"Viewed case study: {case_study_title}. Time: {time_on_page}s. Engagement: {engagement_level}. Intent score: {intent_score}. Pain point: {inferred_pain_point}. Stage: {research_stage}."}',
          },
        ],
        [
          {
            name: "results[0].id",
            customName: "hs_contact_id",
            type: "string",
          },
        ],
      ),

      // 7. Targeted follow-up email
      emailNode(
        "7",
        "Send Targeted Follow-Up Email",
        1480,
        "{viewer_email}",
        "{follow_up_subject}",
        "{follow_up_message}",
      ),

      // 8. Deal acceleration check condition
      n(
        "8",
        "condition",
        "Check if Deal Acceleration Opportunity Exists",
        1720,
        {
          conditionDetails: {
            conditions: [
              {
                variable: "follow_up_priority",
                operator: "equals",
                value: "high",
              },
            ],
            logicalOperator: "AND",
          },
        },
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1735. CHALLENGE EXTRACTION FROM 10-K (8 nodes)
  // ===================================================================

  "Challenge Extraction from 10-K": {
    summary:
      "Fires when a target account is flagged via webhook, retrieves the SEC 10-K filing via API, processes the document with runCode, AI-extracts strategic challenges, AI-maps outreach angles, ranks challenge priorities with runCode, updates CRM intelligence in HubSpot, and delivers a rep briefing via Slack.",
    nodes: [
      trigger(
        "Webhook: Target account flagged for 10-K analysis",
        "webhook_app",
        {
          source: "sales_ops",
          event: "account.flagged_10k_analysis",
          howItStarts:
            "Fires when a sales rep or operations team flags a target account for 10-K filing analysis, indicating they need competitive intelligence extracted from the company's SEC filings",
          setup:
            "Configure internal webhook for account flagging. Set SEC EDGAR API access. Set OpenAI API key for challenge extraction and outreach mapping. Configure HubSpot token for CRM updates. Configure Slack webhook for rep briefings.",
          inputFields: [],
        },
      ),

      // 2. AI extract account data
      aiNode(
        "2",
        "AI Extract Target Account Data",
        280,
        "You are a CRM event parser. Extract the company name, ticker symbol, CIK number, account owner email, account ID, industry, deal stage, and any specific research focus from the raw webhook payload. Respond JSON: {company_name, ticker_symbol, cik_number, owner_email, account_id, industry, deal_stage, research_focus}",
        "Raw webhook payload: {webhook_payload}. Extract all target account data regardless of field naming conventions.",
        [
          { field: "company_name", as: "company_name", type: "string" },
          { field: "ticker_symbol", as: "ticker_symbol", type: "string" },
          { field: "cik_number", as: "cik_number", type: "string" },
          { field: "owner_email", as: "owner_email", type: "string" },
          { field: "account_id", as: "account_id", type: "string" },
          { field: "industry", as: "target_industry", type: "string" },
          { field: "deal_stage", as: "deal_stage", type: "string" },
          { field: "research_focus", as: "research_focus", type: "string" },
        ],
      ),

      // 3. SEC filing retrieval API
      n("3", "hitApi", "Retrieve 10-K Filing from SEC EDGAR", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://efts.sec.gov/LATEST/search-index?q=%2210-K%22&dateRange=custom&startdt=2024-01-01&enddt=2025-12-31&forms=10-K&ticker={ticker_symbol}",
          headers: [
            { key: "User-Agent", value: "CompanyName admin@company.com" },
            { key: "Accept", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "hits.hits[0]._source.file_date",
              customName: "filing_date",
              type: "string",
            },
            {
              name: "hits.hits[0]._source.display_names[0]",
              customName: "filing_company",
              type: "string",
            },
            {
              name: "hits.hits[0]._id",
              customName: "filing_id",
              type: "string",
            },
            {
              name: "hits.hits[0]._source.file_description",
              customName: "filing_description",
              type: "string",
            },
          ],
        },
      }),

      // 4. Document processing runCode
      n("4", "runCode", "Process and Extract Key Sections from 10-K", 760, {
        runCodeDetails: {
          code: `const filingId = variables.filing_id || '';
const filingDate = variables.filing_date || 'unknown';
const company = variables.filing_company || variables.company_name || '';
const riskFactorsSection = 'Item 1A - Risk Factors';
const mdaSection = 'Item 7 - Management Discussion and Analysis';
const businessSection = 'Item 1 - Business Description';
output.filing_url = 'https://www.sec.gov/Archives/edgar/data/' + variables.cik_number + '/' + filingId;
output.key_sections = riskFactorsSection + ', ' + mdaSection + ', ' + businessSection;
output.filing_year = filingDate.substring(0, 4);
output.analysis_context = company + ' 10-K filed ' + filingDate + '. Focus areas: ' + (variables.research_focus || 'general competitive intelligence') + '. Industry: ' + variables.target_industry;
output.doc_ready = filingId ? 'yes' : 'no';`,
          outputVariables: [
            { name: "filing_url", type: "string" },
            { name: "key_sections", type: "string" },
            { name: "filing_year", type: "string" },
            { name: "analysis_context", type: "string" },
            { name: "doc_ready", type: "string" },
          ],
        },
      }),

      // 5. AI challenge extraction
      aiNode(
        "5",
        "AI Extract Strategic Challenges from 10-K",
        1000,
        "You are a financial analyst and competitive intelligence expert. Based on the 10-K filing context, identify the top 5 strategic challenges, risk factors, and pain points the company faces. For each challenge, assess severity, business impact, and whether your solution could address it. Return JSON: {challenge_1, challenge_1_severity, challenge_1_addressable, challenge_2, challenge_2_severity, challenge_2_addressable, challenge_3, challenge_3_severity, challenge_3_addressable, top_opportunity, competitive_landscape, technology_gaps, regulatory_pressures}",
        "Company: {company_name} ({target_industry}) | Filing: {filing_year} | Context: {analysis_context} | Sections: {key_sections} | Research Focus: {research_focus} | Deal Stage: {deal_stage}",
        [
          { field: "challenge_1", as: "challenge_1", type: "string" },
          {
            field: "challenge_1_severity",
            as: "challenge_1_severity",
            type: "string",
          },
          {
            field: "challenge_1_addressable",
            as: "challenge_1_addressable",
            type: "string",
          },
          { field: "challenge_2", as: "challenge_2", type: "string" },
          {
            field: "challenge_2_severity",
            as: "challenge_2_severity",
            type: "string",
          },
          { field: "challenge_3", as: "challenge_3", type: "string" },
          { field: "top_opportunity", as: "top_opportunity", type: "string" },
          {
            field: "competitive_landscape",
            as: "competitive_landscape",
            type: "string",
          },
          { field: "technology_gaps", as: "technology_gaps", type: "string" },
          {
            field: "regulatory_pressures",
            as: "regulatory_pressures",
            type: "string",
          },
        ],
      ),

      // 6. AI outreach angle mapping + challenge priority ranking runCode
      n("6", "runCode", "Rank Challenge Priorities for Outreach", 1240, {
        runCodeDetails: {
          code: `const challenges = [
  { text: variables.challenge_1, severity: variables.challenge_1_severity, addressable: variables.challenge_1_addressable },
  { text: variables.challenge_2, severity: variables.challenge_2_severity, addressable: 'partial' },
  { text: variables.challenge_3, severity: variables.challenge_3 ? 'medium' : 'low', addressable: 'partial' },
];
const ranked = challenges
  .filter(c => c.text)
  .map((c, i) => ({
    rank: i + 1,
    challenge: c.text,
    severity: c.severity,
    addressable: c.addressable,
    outreach_priority: c.addressable === 'yes' || c.addressable === 'high' ? 'primary' : 'secondary'
  }));
const primaryAngle = ranked.find(r => r.outreach_priority === 'primary');
output.primary_challenge = primaryAngle ? primaryAngle.challenge : ranked[0].challenge;
output.primary_severity = primaryAngle ? primaryAngle.severity : ranked[0].severity;
output.outreach_angle = 'Based on ' + variables.company_name + ' 10-K (' + variables.filing_year + '), the primary addressable challenge is: ' + (primaryAngle ? primaryAngle.challenge : ranked[0].challenge);
output.total_challenges_found = ranked.length;
output.challenge_summary = ranked.map(r => r.rank + '. ' + r.challenge + ' (' + r.severity + ')').join(' | ');`,
          outputVariables: [
            { name: "primary_challenge", type: "string" },
            { name: "primary_severity", type: "string" },
            { name: "outreach_angle", type: "string" },
            { name: "total_challenges_found", type: "number" },
            { name: "challenge_summary", type: "string" },
          ],
        },
      }),

      // 7. CRM intelligence update HubSpot
      hubspotPostNode(
        "7",
        "Update CRM with 10-K Intelligence",
        1480,
        "crm/v3/objects/companies/{account_id}",
        [
          {
            key: "properties",
            value:
              '{"notes_last_updated":"10-K Analysis ({filing_year}): {challenge_summary}. Top opportunity: {top_opportunity}. Tech gaps: {technology_gaps}. Regulatory: {regulatory_pressures}. Competitive: {competitive_landscape}. Outreach angle: {outreach_angle}."}',
          },
        ],
        [{ name: "id", customName: "updated_account_id", type: "string" }],
      ),

      // 8. Rep briefing delivery Slack
      slackNode(
        "8",
        "Deliver 10-K Intelligence Briefing to Rep",
        1720,
        "10-K Intelligence Briefing: *{company_name}* ({ticker_symbol})\\n\\nFiling Year: {filing_year} | Industry: {target_industry}\\nDeal Stage: {deal_stage}\\n\\nTop Challenges ({total_challenges_found} found):\\n{challenge_summary}\\n\\nPrimary Outreach Angle:\\n{outreach_angle}\\n\\nTop Opportunity: {top_opportunity}\\nTechnology Gaps: {technology_gaps}\\nRegulatory Pressures: {regulatory_pressures}\\nCompetitive Landscape: {competitive_landscape}\\n\\nFiling URL: {filing_url}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1736. CHAMPION BLOCKER MAP (8 nodes)
  // ===================================================================

  "Champion Blocker Map": {
    summary:
      "Runs on a scheduled basis to aggregate interaction history from HubSpot, AI-analyzes sentiment and engagement patterns, AI-classifies stakeholder roles as champions or blockers, generates influence maps with runCode, AI-creates engagement strategy recommendations, delivers strategy briefings via Slack, and updates deal intelligence in HubSpot CRM.",
    nodes: [
      trigger("Schedule: Weekly deal stakeholder analysis", "schedule", {
        schedule: "Every Wednesday at 9:00 AM",
        howItStarts:
          "Runs weekly to analyze all active deal stakeholders across the pipeline, mapping champions, blockers, and influencers based on interaction history and engagement patterns",
        setup:
          "Configure HubSpot API token with deal and contact read/write permissions. Set OpenAI API key for sentiment analysis, role classification, and strategy recommendations. Configure Slack webhook for sales team briefings.",
        inputFields: [],
      }),

      // 2. Interaction history aggregation HubSpot
      hubspotGetNode(
        "2",
        "Aggregate Stakeholder Interaction History from HubSpot",
        280,
        'crm/v3/objects/deals/search?filterGroups=[{"filters":[{"propertyName":"dealstage","operator":"NEQ","value":"closedwon"},{"propertyName":"dealstage","operator":"NEQ","value":"closedlost"}]}]&properties=dealname,amount,dealstage,pipeline,num_associated_contacts,notes_last_updated,hs_next_step,closedate',
        [
          { name: "results[0].id", customName: "deal_id", type: "string" },
          {
            name: "results[0].properties.dealname",
            customName: "deal_name",
            type: "string",
          },
          {
            name: "results[0].properties.amount",
            customName: "deal_amount",
            type: "string",
          },
          {
            name: "results[0].properties.dealstage",
            customName: "deal_stage",
            type: "string",
          },
          {
            name: "results[0].properties.num_associated_contacts",
            customName: "stakeholder_count",
            type: "string",
          },
          {
            name: "results[0].properties.notes_last_updated",
            customName: "last_notes",
            type: "string",
          },
          {
            name: "results[0].properties.hs_next_step",
            customName: "next_step",
            type: "string",
          },
          {
            name: "results[0].properties.closedate",
            customName: "close_date",
            type: "string",
          },
          { name: "total", customName: "total_active_deals", type: "number" },
        ],
      ),

      // 3. AI sentiment engagement analysis
      aiNode(
        "3",
        "AI Analyze Stakeholder Sentiment and Engagement",
        520,
        "You are an expert deal analyst specializing in stakeholder mapping. Based on the deal context and interaction notes, analyze the sentiment, engagement frequency, responsiveness, and enthusiasm of stakeholders. Identify positive signals (quick replies, forwarding to colleagues, asking about pricing) and negative signals (delayed responses, objections, ghosting, involving procurement early). Return JSON: {overall_sentiment, engagement_frequency, positive_signals, negative_signals, momentum_direction, risk_factors, champion_indicators, blocker_indicators, neutral_stakeholders}",
        "Deal: {deal_name} | Amount: ${deal_amount} | Stage: {deal_stage} | Stakeholders: {stakeholder_count} | Notes: {last_notes} | Next Step: {next_step} | Close Date: {close_date}",
        [
          {
            field: "overall_sentiment",
            as: "overall_sentiment",
            type: "string",
          },
          {
            field: "engagement_frequency",
            as: "engagement_frequency",
            type: "string",
          },
          { field: "positive_signals", as: "positive_signals", type: "string" },
          { field: "negative_signals", as: "negative_signals", type: "string" },
          {
            field: "momentum_direction",
            as: "momentum_direction",
            type: "string",
          },
          { field: "risk_factors", as: "risk_factors", type: "string" },
          {
            field: "champion_indicators",
            as: "champion_indicators",
            type: "string",
          },
          {
            field: "blocker_indicators",
            as: "blocker_indicators",
            type: "string",
          },
        ],
      ),

      // 4. AI role classification engine
      aiNode(
        "4",
        "AI Classify Stakeholder Roles",
        760,
        "You are a strategic sales advisor. Based on the sentiment analysis, classify each stakeholder interaction pattern into roles: Champion (actively advocates), Blocker (raises objections, slows progress), Influencer (provides input but does not decide), Decision Maker (has budget authority), and Gatekeeper (controls access). Map the power dynamics and recommend who to cultivate and who to navigate around. Return JSON: {champion_names, blocker_names, influencer_names, decision_maker_names, power_dynamic_summary, relationship_risk, champion_strength_score, blocker_threat_score, deal_health_rating}",
        "Deal: {deal_name} | Sentiment: {overall_sentiment} | Momentum: {momentum_direction} | Champions: {champion_indicators} | Blockers: {blocker_indicators} | Positive: {positive_signals} | Negative: {negative_signals} | Risks: {risk_factors} | Engagement: {engagement_frequency}",
        [
          { field: "champion_names", as: "champion_names", type: "string" },
          { field: "blocker_names", as: "blocker_names", type: "string" },
          { field: "influencer_names", as: "influencer_names", type: "string" },
          {
            field: "decision_maker_names",
            as: "decision_maker_names",
            type: "string",
          },
          {
            field: "power_dynamic_summary",
            as: "power_dynamics",
            type: "string",
          },
          {
            field: "champion_strength_score",
            as: "champion_strength",
            type: "string",
          },
          {
            field: "blocker_threat_score",
            as: "blocker_threat",
            type: "string",
          },
          { field: "deal_health_rating", as: "deal_health", type: "string" },
        ],
      ),

      // 5. Influence map generation runCode
      n("5", "runCode", "Generate Influence Map Data", 1000, {
        runCodeDetails: {
          code: `const champions = (variables.champion_names || '').split(',').map(n => n.trim()).filter(Boolean);
const blockers = (variables.blocker_names || '').split(',').map(n => n.trim()).filter(Boolean);
const influencers = (variables.influencer_names || '').split(',').map(n => n.trim()).filter(Boolean);
const decisionMakers = (variables.decision_maker_names || '').split(',').map(n => n.trim()).filter(Boolean);
const champStrength = parseInt(variables.champion_strength) || 0;
const blockerThreat = parseInt(variables.blocker_threat) || 0;
const netInfluence = champStrength - blockerThreat;
let dealRisk = 'low';
if (blockers.length > champions.length) dealRisk = 'high';
else if (blockers.length === champions.length) dealRisk = 'medium';
if (decisionMakers.length === 0) dealRisk = 'critical';
output.influence_map = 'Champions(' + champions.length + '): ' + champions.join(', ') + ' | Blockers(' + blockers.length + '): ' + blockers.join(', ') + ' | Influencers(' + influencers.length + '): ' + influencers.join(', ') + ' | Decision Makers(' + decisionMakers.length + '): ' + decisionMakers.join(', ');
output.net_influence_score = netInfluence;
output.deal_risk_level = dealRisk;
output.champion_count = champions.length;
output.blocker_count = blockers.length;
output.needs_intervention = blockerThreat > 60 || dealRisk === 'high' || dealRisk === 'critical' ? 'yes' : 'no';`,
          outputVariables: [
            { name: "influence_map", type: "string" },
            { name: "net_influence_score", type: "number" },
            { name: "deal_risk_level", type: "string" },
            { name: "champion_count", type: "number" },
            { name: "blocker_count", type: "number" },
            { name: "needs_intervention", type: "string" },
          ],
        },
      }),

      // 6. AI engagement strategy recommendations
      aiNode(
        "6",
        "AI Generate Engagement Strategy Recommendations",
        1240,
        "You are a strategic deal coach. Based on the champion/blocker map, recommend specific engagement tactics. For champions: how to empower them to sell internally. For blockers: how to address their concerns or route around them. For decision makers: how to get direct access. Provide a week-by-week action plan. Return JSON: {champion_strategy, blocker_strategy, decision_maker_strategy, week_1_actions, week_2_actions, escalation_plan, win_probability_change, key_risk_mitigation}",
        "Deal: {deal_name} (${deal_amount}) | Stage: {deal_stage} | Close: {close_date}\\nInfluence Map: {influence_map}\\nNet Influence: {net_influence_score} | Risk: {deal_risk_level}\\nPower Dynamics: {power_dynamics}\\nDeal Health: {deal_health} | Momentum: {momentum_direction}",
        [
          {
            field: "champion_strategy",
            as: "champion_strategy",
            type: "string",
          },
          { field: "blocker_strategy", as: "blocker_strategy", type: "string" },
          {
            field: "decision_maker_strategy",
            as: "dm_strategy",
            type: "string",
          },
          { field: "week_1_actions", as: "week_1_actions", type: "string" },
          { field: "week_2_actions", as: "week_2_actions", type: "string" },
          { field: "escalation_plan", as: "escalation_plan", type: "string" },
          {
            field: "win_probability_change",
            as: "win_prob_change",
            type: "string",
          },
          {
            field: "key_risk_mitigation",
            as: "risk_mitigation",
            type: "string",
          },
        ],
      ),

      // 7. Sales rep strategy briefing Slack
      slackNode(
        "7",
        "Deliver Champion-Blocker Strategy Briefing via Slack",
        1480,
        "Champion-Blocker Map: *{deal_name}* (${deal_amount})\\nStage: {deal_stage} | Close: {close_date} | Risk: {deal_risk_level}\\n\\nInfluence Map:\\n{influence_map}\\nNet Influence Score: {net_influence_score}\\n\\nChampion Strategy: {champion_strategy}\\nBlocker Strategy: {blocker_strategy}\\nDecision Maker Strategy: {dm_strategy}\\n\\nThis Week: {week_1_actions}\\nNext Week: {week_2_actions}\\nEscalation: {escalation_plan}\\nRisk Mitigation: {risk_mitigation}\\nProjected Win Prob Change: {win_prob_change}",
      ),

      // 8. CRM deal intelligence update HubSpot
      hubspotPostNode(
        "8",
        "Update Deal Intelligence in HubSpot CRM",
        1720,
        "crm/v3/objects/deals/{deal_id}",
        [
          {
            key: "properties",
            value:
              '{"notes_last_updated":"Champion-Blocker Map: {influence_map}. Net influence: {net_influence_score}. Risk: {deal_risk_level}. Health: {deal_health}. Champion strategy: {champion_strategy}. Blocker strategy: {blocker_strategy}. Week 1: {week_1_actions}.","hs_next_step":"{week_1_actions}"}',
          },
        ],
        [
          { name: "id", customName: "updated_deal_id", type: "string" },
          {
            name: "properties.hs_next_step",
            customName: "updated_next_step",
            type: "string",
          },
        ],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1737. CHANNEL RESELLER OUTREACH (8 nodes)
  // ===================================================================

  "Channel Reseller Outreach": {
    summary:
      "Fires when a reseller prospect is identified via webhook, researches and scores the prospect via Clearbit API, AI-analyzes margin and territory fit, AI-generates personalized outreach, sends a multi-touch email sequence, books meetings via Calendly API integration, branches on partner onboarding qualification, and tracks the pipeline in Sheets.",
    nodes: [
      trigger("Webhook: Reseller prospect identified", "webhook_app", {
        source: "partner_team",
        event: "reseller.prospect_identified",
        howItStarts:
          "Fires when the partnerships team identifies a new potential channel reseller, sending the raw prospect data including company info, territory, and estimated market reach",
        setup:
          "Configure internal webhook for reseller prospect identification. Set Clearbit API key for prospect research. Set OpenAI API key for analysis and outreach generation. Configure email credentials. Set Calendly API key for meeting scheduling. Configure Google Sheets for pipeline tracking.",
        inputFields: [],
      }),

      // 2. AI extract prospect data
      aiNode(
        "2",
        "AI Extract Reseller Prospect Data",
        280,
        "You are a partner prospect data parser. Extract the company name, contact name, contact email, territory, estimated market reach, current product portfolio, years in business, annual revenue estimate, referral source, and partnership tier interest from the raw webhook payload. Respond JSON: {company_name, contact_name, contact_email, territory, market_reach, product_portfolio, years_in_business, annual_revenue, referral_source, tier_interest}",
        "Raw webhook payload: {webhook_payload}. Extract all reseller prospect data regardless of field naming conventions.",
        [
          { field: "company_name", as: "reseller_company", type: "string" },
          { field: "contact_name", as: "contact_name", type: "string" },
          { field: "contact_email", as: "contact_email", type: "string" },
          { field: "territory", as: "territory", type: "string" },
          { field: "market_reach", as: "market_reach", type: "string" },
          {
            field: "product_portfolio",
            as: "product_portfolio",
            type: "string",
          },
          {
            field: "years_in_business",
            as: "years_in_business",
            type: "string",
          },
          { field: "annual_revenue", as: "annual_revenue", type: "string" },
          { field: "referral_source", as: "referral_source", type: "string" },
          { field: "tier_interest", as: "tier_interest", type: "string" },
        ],
      ),

      // 3. Prospect research scoring Clearbit API
      n("3", "hitApi", "Research and Score Reseller via Clearbit", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://company.clearbit.com/v2/companies/find?domain={reseller_company}",
          headers: [
            { key: "Authorization", value: "Bearer {clearbit_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "name",
              customName: "enriched_company_name",
              type: "string",
            },
            {
              name: "metrics.employees",
              customName: "employee_count",
              type: "string",
            },
            {
              name: "category.industry",
              customName: "reseller_industry",
              type: "string",
            },
            {
              name: "metrics.annualRevenue",
              customName: "enriched_revenue",
              type: "string",
            },
            {
              name: "geo.country",
              customName: "company_country",
              type: "string",
            },
            { name: "tech", customName: "tech_stack", type: "string" },
          ],
        },
      }),

      // 4. AI margin territory analysis
      aiNode(
        "4",
        "AI Analyze Margin and Territory Fit",
        760,
        "You are a channel partnerships analyst. Evaluate the reseller prospect for margin opportunity, territory coverage, portfolio complementarity, and strategic value. Score the partner fit 0-100 and recommend the partnership tier. Return JSON: {partner_fit_score, margin_potential, territory_coverage_grade, portfolio_synergy, strategic_value, recommended_tier, revenue_projection_12mo, onboarding_complexity, risk_assessment, competitive_overlap}",
        "Reseller: {enriched_company_name} | Industry: {reseller_industry} | Employees: {employee_count} | Revenue: {enriched_revenue} | Country: {company_country} | Territory: {territory} | Market Reach: {market_reach} | Portfolio: {product_portfolio} | Years: {years_in_business} | Tech: {tech_stack} | Tier Interest: {tier_interest}",
        [
          {
            field: "partner_fit_score",
            as: "partner_fit_score",
            type: "string",
          },
          { field: "margin_potential", as: "margin_potential", type: "string" },
          {
            field: "territory_coverage_grade",
            as: "territory_grade",
            type: "string",
          },
          {
            field: "portfolio_synergy",
            as: "portfolio_synergy",
            type: "string",
          },
          { field: "recommended_tier", as: "recommended_tier", type: "string" },
          {
            field: "revenue_projection_12mo",
            as: "revenue_projection",
            type: "string",
          },
          {
            field: "onboarding_complexity",
            as: "onboarding_complexity",
            type: "string",
          },
          { field: "risk_assessment", as: "risk_assessment", type: "string" },
        ],
      ),

      // 5. AI personalized outreach generation
      aiNode(
        "5",
        "AI Generate Personalized Reseller Outreach",
        1000,
        "You are an expert channel partnerships copywriter. Craft a compelling partnership outreach email for the reseller prospect. Highlight the margin opportunity, territory exclusivity potential, portfolio fit, and mutual growth. Be professional yet personable. Return JSON: {email_subject, email_body, value_highlights, margin_details, territory_offer, next_steps_proposal}",
        "Reseller: {enriched_company_name} | Contact: {contact_name} | Fit Score: {partner_fit_score} | Margin: {margin_potential} | Territory: {territory_grade} | Synergy: {portfolio_synergy} | Recommended Tier: {recommended_tier} | Revenue Potential: {revenue_projection} | Referral: {referral_source}",
        [
          { field: "email_subject", as: "outreach_subject", type: "string" },
          { field: "email_body", as: "outreach_body", type: "string" },
          { field: "value_highlights", as: "value_highlights", type: "string" },
          { field: "margin_details", as: "margin_details", type: "string" },
          { field: "territory_offer", as: "territory_offer", type: "string" },
          { field: "next_steps_proposal", as: "next_steps", type: "string" },
        ],
      ),

      // 6. Multi-touch outreach sequence email
      emailNode(
        "6",
        "Send Reseller Partnership Outreach Email",
        1240,
        "{contact_email}",
        "{outreach_subject}",
        "{outreach_body}",
      ),

      // 7. Partner onboarding trigger condition
      n(
        "7",
        "condition",
        "Check if Partner Qualifies for Fast-Track Onboarding",
        1480,
        {
          conditionDetails: {
            conditions: [
              {
                variable: "recommended_tier",
                operator: "equals",
                value: "gold",
              },
            ],
            logicalOperator: "AND",
          },
        },
      ),

      // 8a. Pipeline tracking Sheets (True — qualified)
      sheetsNode(
        "8",
        "Track Qualified Reseller in Pipeline",
        1720,
        "ResellerPipeline",
        `[["${new Date().toISOString().split("T")[0]}", "{enriched_company_name}", "{contact_name}", "{contact_email}", "{territory}", "{partner_fit_score}", "{recommended_tier}", "{margin_potential}", "{revenue_projection}", "{territory_grade}", "{referral_source}", "fast_track"]]`,
      ),

      // 8b. Pipeline tracking Sheets (False — standard)
      sheetsNode(
        "9",
        "Track Standard Reseller in Pipeline",
        1720,
        "ResellerPipeline",
        `[["${new Date().toISOString().split("T")[0]}", "{enriched_company_name}", "{contact_name}", "{contact_email}", "{territory}", "{partner_fit_score}", "{recommended_tier}", "{margin_potential}", "{revenue_projection}", "{territory_grade}", "{referral_source}", "standard"]]`,
        400,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
      e("7", "9", "output_2"),
    ],
  },

  // ===================================================================
  // 1738. CLAY DIRECT EMAIL CAMPAIGN (8 nodes)
  // ===================================================================

  "Clay Direct Email Campaign": {
    summary:
      "Fires when leads are imported from Clay via webhook, validates lead quality with runCode, AI-personalizes email content, configures SendGrid campaign with runCode, dispatches rate-limited emails with runCode, branches on engagement tracking condition, syncs engaged leads to HubSpot CRM, and reports campaign performance via Slack.",
    nodes: [
      trigger("Webhook: Clay lead import received", "webhook_app", {
        source: "clay",
        event: "enrichment.batch_complete",
        howItStarts:
          "Fires when a batch of enriched leads from Clay is ready for email outreach, sending the raw enrichment data with contact details, firmographic data, and personalization fields",
        setup:
          "Configure Clay webhook for batch completion events. Set OpenAI API key for email personalization. Configure SendGrid API key for email dispatch. Set HubSpot token for CRM sync. Configure Slack webhook for campaign reporting.",
        inputFields: [],
      }),

      // 2. AI extract Clay data
      aiNode(
        "2",
        "AI Extract Clay Import Data",
        280,
        "You are a Clay data parser. Extract the lead email, first name, last name, company name, job title, industry, company size, LinkedIn URL, personalization snippet from Clay enrichment, and the batch ID from the raw webhook payload. Respond JSON: {lead_email, first_name, last_name, company_name, job_title, industry, company_size, linkedin_url, clay_snippet, batch_id, total_leads}",
        "Raw webhook payload: {webhook_payload}. Extract all Clay enrichment data regardless of field naming conventions.",
        [
          { field: "lead_email", as: "lead_email", type: "string" },
          { field: "first_name", as: "first_name", type: "string" },
          { field: "last_name", as: "last_name", type: "string" },
          { field: "company_name", as: "company_name", type: "string" },
          { field: "job_title", as: "job_title", type: "string" },
          { field: "industry", as: "lead_industry", type: "string" },
          { field: "company_size", as: "company_size", type: "string" },
          { field: "linkedin_url", as: "linkedin_url", type: "string" },
          { field: "clay_snippet", as: "clay_snippet", type: "string" },
          { field: "batch_id", as: "batch_id", type: "string" },
          { field: "total_leads", as: "total_leads", type: "string" },
        ],
      ),

      // 3. Lead quality validation runCode
      n("3", "runCode", "Validate Lead Quality from Clay", 520, {
        runCodeDetails: {
          code: `const email = (variables.lead_email || '').trim().toLowerCase();
const company = (variables.company_name || '').trim();
const title = (variables.job_title || '').toLowerCase();
const emailRegex = /^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$/;
const isValid = emailRegex.test(email);
const isCorporate = !['gmail.com','yahoo.com','hotmail.com','outlook.com','aol.com'].some(d => email.endsWith(d));
const isDecisionMaker = ['vp','director','head','chief','cto','ceo','coo','cfo','svp','evp','founder','owner','president','manager'].some(k => title.includes(k));
let quality_tier = 'low';
if (isValid && isCorporate && isDecisionMaker && company) quality_tier = 'high';
else if (isValid && isCorporate && company) quality_tier = 'medium';
else if (isValid) quality_tier = 'low';
else quality_tier = 'invalid';
output.validated_email = email;
output.is_valid = isValid ? 'true' : 'false';
output.is_corporate = isCorporate ? 'true' : 'false';
output.is_decision_maker = isDecisionMaker ? 'true' : 'false';
output.quality_tier = quality_tier;
output.send_eligible = quality_tier !== 'invalid' ? 'true' : 'false';`,
          outputVariables: [
            { name: "validated_email", type: "string" },
            { name: "is_valid", type: "string" },
            { name: "is_corporate", type: "string" },
            { name: "is_decision_maker", type: "string" },
            { name: "quality_tier", type: "string" },
            { name: "send_eligible", type: "string" },
          ],
        },
      }),

      // 4. AI email personalization
      aiNode(
        "4",
        "AI Personalize Email Content",
        760,
        "You are an expert cold email copywriter. Using the Clay enrichment snippet and lead data, craft a hyper-personalized cold email that feels handwritten. Reference something specific about their company or role. Keep it under 150 words. No generic templates. Return JSON: {subject_line, opening_line, value_proposition, call_to_action, ps_line, personalization_approach}",
        "Lead: {first_name} {last_name} | Title: {job_title} at {company_name} | Industry: {lead_industry} | Size: {company_size} | Clay Snippet: {clay_snippet} | LinkedIn: {linkedin_url} | Quality: {quality_tier}",
        [
          { field: "subject_line", as: "email_subject", type: "string" },
          { field: "opening_line", as: "opening_line", type: "string" },
          { field: "value_proposition", as: "value_prop", type: "string" },
          { field: "call_to_action", as: "email_cta", type: "string" },
          { field: "ps_line", as: "ps_line", type: "string" },
          {
            field: "personalization_approach",
            as: "personalization_approach",
            type: "string",
          },
        ],
      ),

      // 5. SendGrid campaign configuration runCode
      n("5", "runCode", "Configure SendGrid Campaign Parameters", 1000, {
        runCodeDetails: {
          code: `const quality = variables.quality_tier || 'medium';
const sendDelay = quality === 'high' ? 0 : quality === 'medium' ? 30 : 60;
const trackOpens = true;
const trackClicks = true;
const unsubGroup = 'cold_outreach';
const fromName = 'Sales Team';
const replyTo = 'sales@company.com';
const fullBody = variables.opening_line + '\\n\\n' + variables.value_prop + '\\n\\n' + variables.email_cta;
const fullBodyWithPs = variables.ps_line ? fullBody + '\\n\\nP.S. ' + variables.ps_line : fullBody;
output.send_delay_seconds = sendDelay;
output.tracking_config = 'opens:' + trackOpens + ',clicks:' + trackClicks;
output.unsub_group = unsubGroup;
output.from_name = fromName;
output.reply_to = replyTo;
output.full_email_body = fullBodyWithPs;
output.campaign_label = 'clay_batch_' + variables.batch_id + '_' + quality;`,
          outputVariables: [
            { name: "send_delay_seconds", type: "number" },
            { name: "tracking_config", type: "string" },
            { name: "unsub_group", type: "string" },
            { name: "from_name", type: "string" },
            { name: "reply_to", type: "string" },
            { name: "full_email_body", type: "string" },
            { name: "campaign_label", type: "string" },
          ],
        },
      }),

      // 6. Engagement tracking condition
      n("6", "condition", "Check if Lead is Send-Eligible", 1240, {
        conditionDetails: {
          conditions: [
            { variable: "send_eligible", operator: "equals", value: "true" },
          ],
          logicalOperator: "AND",
        },
      }),

      // 7a. CRM sync HubSpot (True — eligible)
      hubspotPostNode(
        "7",
        "Sync Eligible Lead to HubSpot CRM",
        1480,
        "crm/v3/objects/contacts",
        [
          {
            key: "properties",
            value:
              '{"email":"{validated_email}","firstname":"{first_name}","lastname":"{last_name}","company":"{company_name}","jobtitle":"{job_title}","industry":"{lead_industry}","hs_lead_status":"OUTREACH_SENT","lead_source":"clay_import","notes_last_updated":"Clay campaign: {campaign_label}. Quality: {quality_tier}. Personalization: {personalization_approach}."}',
          },
        ],
        [
          { name: "id", customName: "hs_contact_id", type: "string" },
          {
            name: "properties.createdate",
            customName: "contact_created",
            type: "string",
          },
        ],
      ),

      // 7b. Campaign performance dashboard Slack (False — ineligible)
      slackNode(
        "8",
        "Report Ineligible Lead to Campaign Dashboard",
        1480,
        "Clay Campaign: Lead skipped (ineligible)\\nEmail: {validated_email} | Quality: {quality_tier}\\nCompany: {company_name} | Title: {job_title}\\nValid: {is_valid} | Corporate: {is_corporate}\\nBatch: {batch_id} | Total in batch: {total_leads}",
        400,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("6", "8", "output_2"),
    ],
  },

  // ===================================================================
  // 1739. CLOSED-LOST PARTNER RECYCLE (8 nodes)
  // ===================================================================

  "Closed-Lost Partner Recycle": {
    summary:
      "Fires when a deal is marked closed-lost via webhook, analyzes the loss reason with runCode, branches on partner match condition, AI-drafts a partner introduction, notifies the partner via Slack, sends a prospect referral email, tracks the referral in Sheets, and preserves the relationship with a delay.",
    nodes: [
      trigger("Webhook: Deal closed-lost for partner recycle", "webhook_app", {
        source: "hubspot",
        event: "deal.closed_lost",
        howItStarts:
          "Fires when a deal is marked as closed-lost in the CRM, triggering evaluation for partner referral recycling instead of letting the prospect go cold",
        setup:
          "Configure HubSpot webhook for deal stage changes to closed-lost. Set OpenAI API key for introduction drafting. Configure Slack webhook for partner notifications. Set email credentials for referral outreach. Configure Google Sheets for referral tracking.",
        inputFields: [],
      }),

      // 2. AI extract deal data
      aiNode(
        "2",
        "AI Extract Closed-Lost Deal Data",
        280,
        "You are a CRM deal parser. Extract the deal name, deal amount, contact email, contact name, company name, loss reason, competitor involved, deal owner email, deal ID, product interest, and pipeline from the raw webhook payload. Respond JSON: {deal_name, deal_amount, contact_email, contact_name, company_name, loss_reason, competitor, owner_email, deal_id, product_interest, pipeline}",
        "Raw webhook payload: {webhook_payload}. Extract all closed-lost deal data regardless of field naming conventions.",
        [
          { field: "deal_name", as: "deal_name", type: "string" },
          { field: "deal_amount", as: "deal_amount", type: "string" },
          { field: "contact_email", as: "contact_email", type: "string" },
          { field: "contact_name", as: "contact_name", type: "string" },
          { field: "company_name", as: "company_name", type: "string" },
          { field: "loss_reason", as: "loss_reason", type: "string" },
          { field: "competitor", as: "competitor_name", type: "string" },
          { field: "owner_email", as: "owner_email", type: "string" },
          { field: "deal_id", as: "deal_id", type: "string" },
          { field: "product_interest", as: "product_interest", type: "string" },
          { field: "pipeline", as: "deal_pipeline", type: "string" },
        ],
      ),

      // 3. Loss reason analysis runCode
      n("3", "runCode", "Analyze Loss Reason for Partner Match", 520, {
        runCodeDetails: {
          code: `const reason = (variables.loss_reason || '').toLowerCase();
const competitor = (variables.competitor_name || '').toLowerCase();
const amount = parseInt(variables.deal_amount) || 0;
let partner_fit = 'none';
let partner_type = '';
let recyclable = 'no';
if (reason.includes('price') || reason.includes('budget') || reason.includes('cost')) {
  partner_fit = 'reseller';
  partner_type = 'value_added_reseller';
  recyclable = 'yes';
} else if (reason.includes('feature') || reason.includes('functionality') || reason.includes('integration')) {
  partner_fit = 'technology_partner';
  partner_type = 'integration_partner';
  recyclable = 'yes';
} else if (reason.includes('service') || reason.includes('support') || reason.includes('implementation')) {
  partner_fit = 'service_partner';
  partner_type = 'consulting_partner';
  recyclable = 'yes';
} else if (reason.includes('region') || reason.includes('local') || reason.includes('language')) {
  partner_fit = 'regional_partner';
  partner_type = 'local_reseller';
  recyclable = 'yes';
} else if (competitor && amount > 5000) {
  partner_fit = 'competitive_displacement';
  partner_type = 'strategic_partner';
  recyclable = 'yes';
}
output.partner_fit = partner_fit;
output.partner_type = partner_type;
output.recyclable = recyclable;
output.recycle_reason = reason + ' | competitor: ' + competitor + ' | amount: ' + amount;`,
          outputVariables: [
            { name: "partner_fit", type: "string" },
            { name: "partner_type", type: "string" },
            { name: "recyclable", type: "string" },
            { name: "recycle_reason", type: "string" },
          ],
        },
      }),

      // 4. Partner match engine condition
      n("4", "condition", "Check if Deal is Recyclable to Partner", 760, {
        conditionDetails: {
          conditions: [
            { variable: "recyclable", operator: "equals", value: "yes" },
          ],
          logicalOperator: "AND",
        },
      }),

      // 5a. AI partner introduction draft (True path)
      aiNode(
        "5",
        "AI Draft Partner Introduction",
        1000,
        "You are a partnerships communications specialist. Draft a warm introduction email connecting the closed-lost prospect with the appropriate partner. Explain the referral context without revealing the loss reason directly. Position the partner as a better fit for their specific needs. Return JSON: {intro_subject, intro_body, partner_value_prop, prospect_context, referral_fee_note, follow_up_expectation}",
        "Prospect: {contact_name} at {company_name} | Product Interest: {product_interest} | Loss Reason: {loss_reason} | Partner Type Needed: {partner_type} | Deal Value: ${deal_amount} | Pipeline: {deal_pipeline}",
        [
          { field: "intro_subject", as: "intro_subject", type: "string" },
          { field: "intro_body", as: "intro_body", type: "string" },
          {
            field: "partner_value_prop",
            as: "partner_value_prop",
            type: "string",
          },
          { field: "prospect_context", as: "prospect_context", type: "string" },
          {
            field: "referral_fee_note",
            as: "referral_fee_note",
            type: "string",
          },
          {
            field: "follow_up_expectation",
            as: "follow_up_expectation",
            type: "string",
          },
        ],
      ),

      // 6. Partner notification Slack
      slackNode(
        "6",
        "Notify Partner Team via Slack",
        1240,
        "Partner Recycle Opportunity\\n\\nDeal: *{deal_name}* (${deal_amount})\\nProspect: {contact_name} at {company_name}\\nLoss Reason: {loss_reason}\\nPartner Type: {partner_type} ({partner_fit})\\n\\nReferral Context: {prospect_context}\\nPartner Value Prop: {partner_value_prop}\\nReferral Fee: {referral_fee_note}\\nExpected Follow-Up: {follow_up_expectation}\\n\\nIntroduction email is being sent to the prospect.",
      ),

      // 7. Prospect referral email
      emailNode(
        "7",
        "Send Partner Referral Email to Prospect",
        1480,
        "{contact_email}",
        "{intro_subject}",
        "{intro_body}",
      ),

      // 8. Referral tracking Sheets
      sheetsNode(
        "8",
        "Track Partner Referral in Sheets",
        1720,
        "PartnerRecycles",
        `[["${new Date().toISOString().split("T")[0]}", "{deal_name}", "{deal_amount}", "{contact_name}", "{company_name}", "{loss_reason}", "{partner_type}", "{partner_fit}", "{referral_fee_note}", "{follow_up_expectation}", "referral_sent"]]`,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1740. CLOSED-LOST RECYCLE WORKFLOW (9 nodes)
  // ===================================================================

  "Closed-Lost Recycle Workflow": {
    summary:
      "Runs daily to detect trigger events from intent data APIs, matches product updates with runCode, qualifies re-engagement with runCode, AI-generates a re-engagement message, clones opportunities in HubSpot, notifies reps via Slack, sends re-engagement outreach email, and tracks recycle outcomes in Sheets.",
    nodes: [
      trigger("Schedule: Daily closed-lost recycle scan", "schedule", {
        schedule: "Every day at 8:00 AM",
        howItStarts:
          "Runs daily to scan for trigger events (funding rounds, leadership changes, product launches) affecting previously closed-lost accounts, identifying opportunities for re-engagement",
        setup:
          "Configure intent data API access (Bombora or G2). Set OpenAI API key for re-engagement messaging. Configure HubSpot API token for opportunity management. Configure Slack webhook for rep notifications. Set email credentials. Set Google Sheets for outcome tracking.",
        inputFields: [],
      }),

      // 2. Trigger event detection API
      n("2", "hitApi", "Detect Trigger Events for Lost Accounts", 280, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.bombora.com/v1/surge/accounts",
          headers: [
            { key: "Authorization", value: "Bearer {bombora_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "filter",
              value:
                '{"account_status":"closed_lost","surge_score_min":70,"date_range":"last_30_days"}',
            },
          ],
          variables: [
            {
              name: "accounts[0].company_name",
              customName: "trigger_company",
              type: "string",
            },
            {
              name: "accounts[0].surge_score",
              customName: "surge_score",
              type: "string",
            },
            {
              name: "accounts[0].topics",
              customName: "surge_topics",
              type: "string",
            },
            {
              name: "accounts[0].contact_email",
              customName: "contact_email",
              type: "string",
            },
            {
              name: "accounts[0].contact_name",
              customName: "contact_name",
              type: "string",
            },
            {
              name: "accounts[0].deal_id",
              customName: "original_deal_id",
              type: "string",
            },
            {
              name: "total",
              customName: "total_trigger_accounts",
              type: "number",
            },
          ],
        },
      }),

      // 3. Product update matcher runCode
      n("3", "runCode", "Match Trigger Events to Product Updates", 760, {
        runCodeDetails: {
          code: `const topics = (variables.surge_topics || '').toLowerCase();
const productUpdates = {
  'security': { update: 'SOC 2 Type II certification completed', feature: 'Enterprise security suite', relevance: 'high' },
  'integration': { update: 'New native integrations with Salesforce, HubSpot, Slack', feature: 'Universal API connector', relevance: 'high' },
  'automation': { update: 'AI-powered workflow automation engine launched', feature: 'Intelligent automation', relevance: 'high' },
  'analytics': { update: 'Advanced analytics dashboard with predictive insights', feature: 'Analytics Pro', relevance: 'medium' },
  'pricing': { update: 'New flexible pricing tiers for growing teams', feature: 'Starter plan', relevance: 'medium' },
  'api': { update: 'Developer API v3 with GraphQL support', feature: 'Developer Platform', relevance: 'medium' },
};
let matchedUpdate = null;
let matchKey = '';
for (const [key, val] of Object.entries(productUpdates)) {
  if (topics.includes(key)) { matchedUpdate = val; matchKey = key; break; }
}
if (!matchedUpdate) matchedUpdate = { update: 'Significant platform improvements across the board', feature: 'Platform upgrade', relevance: 'low' };
output.matched_update = matchedUpdate.update;
output.matched_feature = matchedUpdate.feature;
output.match_relevance = matchedUpdate.relevance;
output.match_topic = matchKey || 'general';`,
          outputVariables: [
            { name: "matched_update", type: "string" },
            { name: "matched_feature", type: "string" },
            { name: "match_relevance", type: "string" },
            { name: "match_topic", type: "string" },
          ],
        },
      }),

      // 4. Re-engagement qualification runCode
      n("4", "runCode", "Qualify Re-Engagement Opportunity", 520, {
        runCodeDetails: {
          code: `const surgeScore = parseInt(variables.surge_score) || 0;
const relevance = variables.match_relevance || 'low';
let qualificationScore = 0;
qualificationScore += surgeScore >= 80 ? 40 : surgeScore >= 60 ? 25 : 10;
qualificationScore += relevance === 'high' ? 35 : relevance === 'medium' ? 20 : 10;
qualificationScore += variables.contact_email ? 15 : 0;
qualificationScore += variables.original_deal_id ? 10 : 0;
const qualified = qualificationScore >= 50;
const priority = qualificationScore >= 75 ? 'high' : qualificationScore >= 50 ? 'medium' : 'low';
output.qualification_score = qualificationScore;
output.is_qualified = qualified ? 'yes' : 'no';
output.recycle_priority = priority;
output.qualification_factors = 'Surge:' + surgeScore + ' Relevance:' + relevance + ' HasContact:' + (variables.contact_email ? 'yes' : 'no') + ' HasDeal:' + (variables.original_deal_id ? 'yes' : 'no');`,
          outputVariables: [
            { name: "qualification_score", type: "number" },
            { name: "is_qualified", type: "string" },
            { name: "recycle_priority", type: "string" },
            { name: "qualification_factors", type: "string" },
          ],
        },
      }),

      // 5. AI re-engagement message
      aiNode(
        "5",
        "AI Generate Re-Engagement Message",
        1000,
        "You are an expert sales re-engagement copywriter. Craft a compelling re-engagement email for a previously closed-lost prospect. Reference the relevant product update without being pushy. Acknowledge the time that has passed and position the outreach as genuinely helpful. Return JSON: {subject_line, email_body, opening_hook, update_reference, soft_cta, tone}",
        "Prospect: {contact_name} at {trigger_company} | Surge Score: {surge_score} | Topics: {surge_topics} | Matched Update: {matched_update} | Feature: {matched_feature} | Priority: {recycle_priority} | Qualification: {qualification_score}",
        [
          { field: "subject_line", as: "reengagement_subject", type: "string" },
          { field: "email_body", as: "reengagement_body", type: "string" },
          { field: "opening_hook", as: "opening_hook", type: "string" },
          { field: "update_reference", as: "update_reference", type: "string" },
          { field: "soft_cta", as: "soft_cta", type: "string" },
        ],
      ),

      // 6. CRM opportunity clone HubSpot
      hubspotPostNode(
        "6",
        "Clone Opportunity in HubSpot CRM",
        1240,
        "crm/v3/objects/deals",
        [
          {
            key: "properties",
            value:
              '{"dealname":"[RECYCLE] {trigger_company} — {matched_feature}","dealstage":"qualifiedtobuy","pipeline":"default","notes_last_updated":"Recycled from closed-lost deal {original_deal_id}. Trigger: surge score {surge_score} on {surge_topics}. Matched update: {matched_update}. Priority: {recycle_priority}.","hs_lead_status":"RE_ENGAGED"}',
          },
        ],
        [
          { name: "id", customName: "new_deal_id", type: "string" },
          {
            name: "properties.dealname",
            customName: "new_deal_name",
            type: "string",
          },
        ],
      ),

      // 7. Rep assignment notification Slack
      slackNode(
        "7",
        "Notify Rep of Recycled Opportunity via Slack",
        1480,
        "Closed-Lost Recycle Alert\\n\\nCompany: *{trigger_company}* | Surge Score: {surge_score}\\nTopics: {surge_topics}\\nMatched Update: {matched_update} ({matched_feature})\\n\\nQualification: {qualification_score} | Priority: {recycle_priority}\\nFactors: {qualification_factors}\\n\\nNew Deal Created: {new_deal_name} (ID: {new_deal_id})\\nContact: {contact_name} ({contact_email})\\n\\nRe-engagement email is being sent. Please follow up within 48 hours.",
      ),

      // 8. Re-engagement outreach email
      emailNode(
        "8",
        "Send Re-Engagement Outreach Email",
        1720,
        "{contact_email}",
        "{reengagement_subject}",
        "{reengagement_body}",
      ),

      // 9. Recycle outcome tracking Sheets
      sheetsNode(
        "9",
        "Track Recycle Outcome in Sheets",
        1960,
        "RecycleTracker",
        `[["${new Date().toISOString().split("T")[0]}", "{trigger_company}", "{contact_name}", "{surge_score}", "{surge_topics}", "{matched_update}", "{matched_feature}", "{qualification_score}", "{recycle_priority}", "{new_deal_id}", "outreach_sent"]]`,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 1741. CLOSED-WON LOOKALIKE BUILDER (8 nodes)
  // ===================================================================

  "Closed-Won Lookalike Builder": {
    summary:
      "Runs quarterly to extract closed-won deal data from HubSpot, AI-analyzes winning patterns, AI-generates lookalike profiles, builds prospect lists via enrichment API, discovers contacts via API, imports prospects to HubSpot CRM, and distributes to the sales team via Slack.",
    nodes: [
      trigger("Schedule: Quarterly closed-won lookalike analysis", "schedule", {
        schedule: "First day of every quarter at 9:00 AM",
        howItStarts:
          "Runs quarterly to analyze all closed-won deals from the past 12 months, identifying common patterns in industry, size, tech stack, and buying behavior to build lookalike prospect lists",
        setup:
          "Configure HubSpot API token for deal data extraction and prospect import. Set OpenAI API key for pattern analysis and profile generation. Configure enrichment API for prospect discovery. Configure Slack webhook for team distribution.",
        inputFields: [],
      }),

      // 2. Closed-won data extraction HubSpot
      hubspotGetNode(
        "2",
        "Extract Closed-Won Deal Data from HubSpot",
        280,
        'crm/v3/objects/deals/search?filterGroups=[{"filters":[{"propertyName":"dealstage","operator":"EQ","value":"closedwon"},{"propertyName":"closedate","operator":"GTE","value":"{twelve_months_ago}"}]}]&properties=dealname,amount,closedate,pipeline,hs_analytics_source,industry,numberofemployees',
        [
          { name: "results", customName: "won_deals_data", type: "string" },
          { name: "total", customName: "total_won_deals", type: "number" },
          {
            name: "results[0].properties.dealname",
            customName: "sample_deal_name",
            type: "string",
          },
          {
            name: "results[0].properties.amount",
            customName: "sample_deal_amount",
            type: "string",
          },
          {
            name: "results[0].properties.hs_analytics_source",
            customName: "sample_source",
            type: "string",
          },
        ],
      ),

      // 3. AI pattern analysis
      aiNode(
        "3",
        "AI Analyze Closed-Won Patterns",
        520,
        "You are a sales analytics expert. Analyze the closed-won deal data to identify the ideal customer profile (ICP) patterns. Find commonalities in industry, company size, deal size, sales cycle length, lead source, and buying behavior. Create a detailed ICP with scoring weights. Return JSON: {top_industries, ideal_company_size_range, avg_deal_size, common_lead_sources, buying_triggers, sales_cycle_avg_days, icp_description, scoring_weights, exclusion_criteria, confidence_level}",
        "Closed-Won Deals ({total_won_deals} total): {won_deals_data} | Sample: {sample_deal_name} at ${sample_deal_amount} via {sample_source}",
        [
          { field: "top_industries", as: "top_industries", type: "string" },
          {
            field: "ideal_company_size_range",
            as: "ideal_size_range",
            type: "string",
          },
          { field: "avg_deal_size", as: "avg_deal_size", type: "string" },
          {
            field: "common_lead_sources",
            as: "common_sources",
            type: "string",
          },
          { field: "buying_triggers", as: "buying_triggers", type: "string" },
          { field: "icp_description", as: "icp_description", type: "string" },
          { field: "scoring_weights", as: "scoring_weights", type: "string" },
          {
            field: "exclusion_criteria",
            as: "exclusion_criteria",
            type: "string",
          },
        ],
      ),

      // 4. AI lookalike profile generation
      aiNode(
        "4",
        "AI Generate Lookalike Prospect Profiles",
        760,
        "You are a demand generation strategist. Based on the ICP analysis, generate a detailed lookalike prospect targeting criteria that can be used to find new accounts matching the winning pattern. Include firmographic filters, technographic signals, and behavioral indicators. Return JSON: {target_industries, size_filter, revenue_filter, tech_signals, geographic_focus, job_titles_to_target, company_keywords, negative_filters, estimated_tam, search_queries}",
        "ICP: {icp_description} | Industries: {top_industries} | Size: {ideal_size_range} | Avg Deal: {avg_deal_size} | Triggers: {buying_triggers} | Sources: {common_sources} | Weights: {scoring_weights} | Exclusions: {exclusion_criteria}",
        [
          {
            field: "target_industries",
            as: "target_industries",
            type: "string",
          },
          { field: "size_filter", as: "size_filter", type: "string" },
          { field: "revenue_filter", as: "revenue_filter", type: "string" },
          { field: "tech_signals", as: "tech_signals", type: "string" },
          {
            field: "job_titles_to_target",
            as: "target_titles",
            type: "string",
          },
          { field: "company_keywords", as: "company_keywords", type: "string" },
          { field: "estimated_tam", as: "estimated_tam", type: "string" },
          { field: "search_queries", as: "search_queries", type: "string" },
        ],
      ),

      // 5. Prospect list building API
      n("5", "hitApi", "Build Prospect List via Enrichment API", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.apollo.io/v1/mixed_people/search",
          headers: [
            { key: "Content-Type", value: "application/json" },
            { key: "X-Api-Key", value: "{apollo_api_key}" },
          ],
          body: [
            { key: "person_titles", value: "{target_titles}" },
            {
              key: "organization_industry_tag_ids",
              value: "{target_industries}",
            },
            {
              key: "organization_num_employees_ranges",
              value: "{size_filter}",
            },
            { key: "per_page", value: "25" },
          ],
          variables: [
            {
              name: "people[0].name",
              customName: "prospect_name",
              type: "string",
            },
            {
              name: "people[0].email",
              customName: "prospect_email",
              type: "string",
            },
            {
              name: "people[0].title",
              customName: "prospect_title",
              type: "string",
            },
            {
              name: "people[0].organization.name",
              customName: "prospect_company",
              type: "string",
            },
            {
              name: "people[0].organization.industry",
              customName: "prospect_industry",
              type: "string",
            },
            {
              name: "people[0].organization.estimated_num_employees",
              customName: "prospect_size",
              type: "string",
            },
            {
              name: "pagination.total_entries",
              customName: "total_prospects_found",
              type: "number",
            },
          ],
        },
      }),

      // 6. Contact discovery enrichment API
      n("6", "hitApi", "Enrich and Validate Prospect Contact", 1240, {
        hitApiDetails: {
          method: "GET",
          url: "https://person.clearbit.com/v2/people/find?email={prospect_email}",
          headers: [
            { key: "Authorization", value: "Bearer {clearbit_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "name.fullName",
              customName: "verified_name",
              type: "string",
            },
            { name: "title", customName: "verified_title", type: "string" },
            {
              name: "linkedin.handle",
              customName: "prospect_linkedin",
              type: "string",
            },
            {
              name: "company.metrics.annualRevenue",
              customName: "prospect_revenue",
              type: "string",
            },
          ],
        },
      }),

      // 7. CRM prospect import HubSpot
      hubspotPostNode(
        "7",
        "Import Lookalike Prospect to HubSpot",
        1480,
        "crm/v3/objects/contacts",
        [
          {
            key: "properties",
            value:
              '{"email":"{prospect_email}","firstname":"{verified_name}","company":"{prospect_company}","jobtitle":"{verified_title}","industry":"{prospect_industry}","numberofemployees":"{prospect_size}","hs_lead_status":"NEW","lead_source":"lookalike_builder","notes_last_updated":"Lookalike match: {icp_description}. Industries: {target_industries}. Size: {size_filter}. TAM: {estimated_tam}."}',
          },
        ],
        [
          { name: "id", customName: "hs_prospect_id", type: "string" },
          {
            name: "properties.createdate",
            customName: "prospect_created",
            type: "string",
          },
        ],
      ),

      // 8. Sales team distribution Slack
      slackNode(
        "8",
        "Distribute Lookalike Prospects to Sales Team",
        1720,
        "Quarterly Lookalike Prospect Report\\n\\nBased on {total_won_deals} closed-won deals analyzed:\\n\\nICP Summary: {icp_description}\\nTop Industries: {top_industries}\\nIdeal Size: {ideal_size_range}\\nAvg Deal: {avg_deal_size}\\nBuying Triggers: {buying_triggers}\\n\\nNew Prospects Found: {total_prospects_found}\\nSample: *{verified_name}* — {verified_title} at {prospect_company} ({prospect_industry}, ~{prospect_size} employees)\\nLinkedIn: {prospect_linkedin}\\n\\nAll prospects imported to HubSpot with 'lookalike_builder' source tag.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1742. CO-MARKETING CAMPAIGN PITCH (8 nodes)
  // ===================================================================

  "Co-Marketing Campaign Pitch": {
    summary:
      "Fires when a potential partner prospect is identified via webhook, analyzes audience overlap with runCode, AI-generates campaign concepts, AI-creates a personalized partnership pitch, sends the pitch email, waits 5 days for response, sends a follow-up sequence email, and integrates meeting booking via Calendly API.",
    nodes: [
      trigger(
        "Webhook: Co-marketing partner prospect identified",
        "webhook_app",
        {
          source: "marketing_ops",
          event: "partner.comarketing_prospect",
          howItStarts:
            "Fires when the marketing team identifies a potential co-marketing partner with complementary audience and non-competing products, suitable for a joint campaign",
          setup:
            "Configure internal webhook for co-marketing partner identification. Set OpenAI API key for campaign concept and pitch generation. Configure email credentials. Set Calendly API key for meeting booking.",
          inputFields: [],
        },
      ),

      // 2. AI extract partner data
      aiNode(
        "2",
        "AI Extract Partner Prospect Data",
        280,
        "You are a marketing partnership data parser. Extract the partner company name, contact name, contact email, partner product/service, partner audience description, estimated audience size, partner website, social channels, content format preferences, and the campaign idea from the raw webhook payload. Respond JSON: {partner_company, contact_name, contact_email, partner_product, partner_audience, audience_size, partner_website, social_channels, content_preferences, campaign_idea}",
        "Raw webhook payload: {webhook_payload}. Extract all co-marketing partner data regardless of field naming conventions.",
        [
          { field: "partner_company", as: "partner_company", type: "string" },
          { field: "contact_name", as: "contact_name", type: "string" },
          { field: "contact_email", as: "contact_email", type: "string" },
          { field: "partner_product", as: "partner_product", type: "string" },
          { field: "partner_audience", as: "partner_audience", type: "string" },
          { field: "audience_size", as: "audience_size", type: "string" },
          { field: "partner_website", as: "partner_website", type: "string" },
          { field: "social_channels", as: "social_channels", type: "string" },
          {
            field: "content_preferences",
            as: "content_preferences",
            type: "string",
          },
          {
            field: "campaign_idea",
            as: "initial_campaign_idea",
            type: "string",
          },
        ],
      ),

      // 3. Audience overlap analysis runCode
      n("3", "runCode", "Analyze Audience Overlap Potential", 520, {
        runCodeDetails: {
          code: `const partnerAudience = (variables.partner_audience || '').toLowerCase();
const audienceSize = parseInt(variables.audience_size) || 0;
const ourAudienceKeywords = ['b2b','saas','marketing','sales','growth','startup','enterprise','technology'];
const overlapKeywords = ourAudienceKeywords.filter(k => partnerAudience.includes(k));
const overlapScore = Math.min(100, (overlapKeywords.length / ourAudienceKeywords.length) * 100);
const reachPotential = audienceSize > 50000 ? 'high' : audienceSize > 10000 ? 'medium' : 'modest';
const complementary = overlapScore > 30 && overlapScore < 80;
const campaignViability = complementary && audienceSize > 5000 ? 'strong' : complementary ? 'moderate' : 'weak';
output.overlap_score = Math.round(overlapScore);
output.overlap_keywords = overlapKeywords.join(', ') || 'minimal';
output.reach_potential = reachPotential;
output.is_complementary = complementary ? 'yes' : 'no';
output.campaign_viability = campaignViability;
output.combined_reach = audienceSize + 25000;`,
          outputVariables: [
            { name: "overlap_score", type: "number" },
            { name: "overlap_keywords", type: "string" },
            { name: "reach_potential", type: "string" },
            { name: "is_complementary", type: "string" },
            { name: "campaign_viability", type: "string" },
            { name: "combined_reach", type: "number" },
          ],
        },
      }),

      // 4. AI campaign concept generation
      aiNode(
        "4",
        "AI Generate Co-Marketing Campaign Concepts",
        760,
        "You are a creative marketing strategist. Generate 3 co-marketing campaign concepts that would leverage both audiences. Each concept should include a campaign name, format (webinar, ebook, challenge, event), estimated leads generated, and resource requirements. Return JSON: {concept_1_name, concept_1_format, concept_1_leads_est, concept_1_description, concept_2_name, concept_2_format, concept_2_leads_est, concept_2_description, concept_3_name, concept_3_format, recommended_concept, success_metrics}",
        "Our Product: B2B SaaS Platform | Partner: {partner_company} ({partner_product}) | Audience Overlap: {overlap_score}% on {overlap_keywords} | Combined Reach: {combined_reach} | Viability: {campaign_viability} | Content Prefs: {content_preferences} | Initial Idea: {initial_campaign_idea}",
        [
          { field: "concept_1_name", as: "concept_1_name", type: "string" },
          { field: "concept_1_format", as: "concept_1_format", type: "string" },
          {
            field: "concept_1_leads_est",
            as: "concept_1_leads",
            type: "string",
          },
          {
            field: "concept_1_description",
            as: "concept_1_desc",
            type: "string",
          },
          { field: "concept_2_name", as: "concept_2_name", type: "string" },
          {
            field: "recommended_concept",
            as: "recommended_concept",
            type: "string",
          },
          { field: "success_metrics", as: "success_metrics", type: "string" },
        ],
      ),

      // 5. AI partnership pitch creation
      aiNode(
        "5",
        "AI Create Personalized Partnership Pitch",
        1000,
        "You are an expert B2B partnerships copywriter. Craft a compelling co-marketing partnership pitch email. Lead with the mutual benefit, present the recommended campaign concept with data, and make the ask specific and easy to say yes to. Return JSON: {pitch_subject, pitch_body, key_stats, mutual_benefit_summary, specific_ask, timeline_proposal}",
        "Partner: {contact_name} at {partner_company} | Audience: {partner_audience} ({audience_size}) | Overlap: {overlap_score}% | Combined Reach: {combined_reach} | Recommended Campaign: {recommended_concept} — {concept_1_name} ({concept_1_format}) est. {concept_1_leads} leads | Viability: {campaign_viability} | Metrics: {success_metrics}",
        [
          { field: "pitch_subject", as: "pitch_subject", type: "string" },
          { field: "pitch_body", as: "pitch_body", type: "string" },
          { field: "key_stats", as: "key_stats", type: "string" },
          {
            field: "mutual_benefit_summary",
            as: "mutual_benefits",
            type: "string",
          },
          { field: "specific_ask", as: "specific_ask", type: "string" },
          {
            field: "timeline_proposal",
            as: "timeline_proposal",
            type: "string",
          },
        ],
      ),

      // 6. Pitch email send
      emailNode(
        "6",
        "Send Co-Marketing Partnership Pitch",
        1240,
        "{contact_email}",
        "{pitch_subject}",
        "{pitch_body}",
      ),

      // 7. Delay 5 days
      n("7", "delayAction", "Wait 5 Days for Response", 1480, {
        delayDetails: { duration: 5, unit: "days" },
      }),

      // 8. Follow-up sequence email
      emailNode(
        "8",
        "Send Partnership Follow-Up Email",
        1720,
        "{contact_email}",
        "Re: {pitch_subject} — quick follow up",
        "Hi {contact_name},\\n\\nI wanted to circle back on the co-marketing opportunity I shared last week.\\n\\nQuick recap: We see a {overlap_score}% audience overlap with a combined reach of {combined_reach}. Our recommended campaign ({concept_1_name}) could generate an estimated {concept_1_leads} leads for both of us.\\n\\nKey stats: {key_stats}\\n\\nI would love to walk you through the concept in 20 minutes. Here is my calendar if any of these times work: https://calendly.com/partnerships/co-marketing\\n\\n{specific_ask}\\n\\nLooking forward to exploring this together.\\n\\nBest regards",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1743. CO-MARKETING WEBINAR PARTNER PITCH (8 nodes)
  // ===================================================================

  "Co-Marketing Webinar Partner Pitch": {
    summary:
      "Fires when a potential webinar partner is identified via webhook, assesses audience authority with runCode, AI-generates webinar concepts, AI-creates a personalized partnership proposal, sends the proposal email, branches on response tracking condition, automates planning kickoff in Asana API, and tracks ROI in Sheets.",
    nodes: [
      trigger("Webhook: Webinar partner prospect identified", "webhook_app", {
        source: "marketing_ops",
        event: "partner.webinar_prospect",
        howItStarts:
          "Fires when the marketing team identifies a potential webinar co-hosting partner based on audience authority, thought leadership, and complementary expertise",
        setup:
          "Configure internal webhook for webinar partner identification. Set OpenAI API key for concept and proposal generation. Configure email credentials. Set Asana API key for project creation. Set Google Sheets for ROI tracking.",
        inputFields: [],
      }),

      // 2. AI extract partner data
      aiNode(
        "2",
        "AI Extract Webinar Partner Data",
        280,
        "You are a marketing partnership parser. Extract the partner company name, speaker name, speaker email, speaker title, speaker expertise, partner audience size, partner audience description, LinkedIn following, content topics, and any preferred webinar dates from the raw webhook payload. Respond JSON: {partner_company, speaker_name, speaker_email, speaker_title, speaker_expertise, audience_size, audience_description, linkedin_following, content_topics, preferred_dates}",
        "Raw webhook payload: {webhook_payload}. Extract all webinar partner data regardless of field naming conventions.",
        [
          { field: "partner_company", as: "partner_company", type: "string" },
          { field: "speaker_name", as: "speaker_name", type: "string" },
          { field: "speaker_email", as: "speaker_email", type: "string" },
          { field: "speaker_title", as: "speaker_title", type: "string" },
          {
            field: "speaker_expertise",
            as: "speaker_expertise",
            type: "string",
          },
          { field: "audience_size", as: "audience_size", type: "string" },
          {
            field: "audience_description",
            as: "audience_description",
            type: "string",
          },
          {
            field: "linkedin_following",
            as: "linkedin_following",
            type: "string",
          },
          { field: "content_topics", as: "content_topics", type: "string" },
          { field: "preferred_dates", as: "preferred_dates", type: "string" },
        ],
      ),

      // 3. Audience authority assessment runCode
      n("3", "runCode", "Assess Audience Authority and Fit", 520, {
        runCodeDetails: {
          code: `const audienceSize = parseInt(variables.audience_size) || 0;
const linkedinFollowing = parseInt(variables.linkedin_following) || 0;
const expertise = (variables.speaker_expertise || '').toLowerCase();
const topics = (variables.content_topics || '').toLowerCase();
let authorityScore = 0;
authorityScore += audienceSize > 25000 ? 30 : audienceSize > 10000 ? 20 : audienceSize > 5000 ? 10 : 5;
authorityScore += linkedinFollowing > 10000 ? 25 : linkedinFollowing > 5000 ? 15 : linkedinFollowing > 1000 ? 10 : 5;
const topicRelevance = ['sales','marketing','growth','revenue','automation','data','analytics','cx'].filter(t => topics.includes(t) || expertise.includes(t));
authorityScore += topicRelevance.length * 8;
const authorityLevel = authorityScore >= 70 ? 'high' : authorityScore >= 40 ? 'medium' : 'emerging';
const expectedRegistrations = Math.round(audienceSize * 0.02 + linkedinFollowing * 0.01);
output.authority_score = Math.min(100, authorityScore);
output.authority_level = authorityLevel;
output.topic_relevance = topicRelevance.join(', ') || 'general';
output.expected_registrations = expectedRegistrations;
output.combined_audience = audienceSize + 30000;
output.partnership_tier = authorityScore >= 60 ? 'premium' : 'standard';`,
          outputVariables: [
            { name: "authority_score", type: "number" },
            { name: "authority_level", type: "string" },
            { name: "topic_relevance", type: "string" },
            { name: "expected_registrations", type: "number" },
            { name: "combined_audience", type: "number" },
            { name: "partnership_tier", type: "string" },
          ],
        },
      }),

      // 4. AI webinar concept generator
      aiNode(
        "4",
        "AI Generate Webinar Concepts",
        760,
        "You are a webinar content strategist. Generate 2 compelling webinar concepts that leverage both speakers' expertise and appeal to both audiences. Each concept should have a title, description, key takeaways, target persona, and estimated attendance. Return JSON: {webinar_1_title, webinar_1_description, webinar_1_takeaways, webinar_1_attendance_est, webinar_2_title, webinar_2_description, webinar_2_takeaways, recommended_webinar, recommended_format, optimal_duration}",
        "Our expertise: B2B sales and marketing automation | Partner: {speaker_name} ({speaker_title}) at {partner_company} | Expertise: {speaker_expertise} | Topics: {content_topics} | Topic Relevance: {topic_relevance} | Authority: {authority_level} | Expected Registrations: {expected_registrations} | Combined Audience: {combined_audience}",
        [
          { field: "webinar_1_title", as: "webinar_1_title", type: "string" },
          {
            field: "webinar_1_description",
            as: "webinar_1_desc",
            type: "string",
          },
          {
            field: "webinar_1_takeaways",
            as: "webinar_1_takeaways",
            type: "string",
          },
          {
            field: "webinar_1_attendance_est",
            as: "webinar_1_attendance",
            type: "string",
          },
          { field: "webinar_2_title", as: "webinar_2_title", type: "string" },
          {
            field: "recommended_webinar",
            as: "recommended_webinar",
            type: "string",
          },
          {
            field: "recommended_format",
            as: "recommended_format",
            type: "string",
          },
          { field: "optimal_duration", as: "optimal_duration", type: "string" },
        ],
      ),

      // 5. AI personalized partnership proposal
      aiNode(
        "5",
        "AI Create Personalized Webinar Proposal",
        1000,
        "You are an expert partnership outreach copywriter. Craft a professional and enticing webinar partnership proposal email. Reference the speaker's specific expertise, highlight the mutual reach, present the recommended webinar concept, and outline the logistics. Return JSON: {proposal_subject, proposal_body, speaker_compliment, audience_benefit, logistics_summary, promotion_plan}",
        "Speaker: {speaker_name} ({speaker_title}) at {partner_company} | Expertise: {speaker_expertise} | Authority: {authority_level} ({authority_score}/100) | Recommended: {recommended_webinar} — {webinar_1_title} ({recommended_format}, {optimal_duration}) | Est. Attendance: {webinar_1_attendance} | Combined Audience: {combined_audience} | Preferred Dates: {preferred_dates}",
        [
          { field: "proposal_subject", as: "proposal_subject", type: "string" },
          { field: "proposal_body", as: "proposal_body", type: "string" },
          {
            field: "speaker_compliment",
            as: "speaker_compliment",
            type: "string",
          },
          { field: "audience_benefit", as: "audience_benefit", type: "string" },
          {
            field: "logistics_summary",
            as: "logistics_summary",
            type: "string",
          },
          { field: "promotion_plan", as: "promotion_plan", type: "string" },
        ],
      ),

      // 6. Proposal email outreach
      emailNode(
        "6",
        "Send Webinar Partnership Proposal",
        1240,
        "{speaker_email}",
        "{proposal_subject}",
        "{proposal_body}",
      ),

      // 7. Response tracking condition
      n("7", "condition", "Check if Partner is Premium Tier", 1480, {
        conditionDetails: {
          conditions: [
            {
              variable: "partnership_tier",
              operator: "equals",
              value: "premium",
            },
          ],
          logicalOperator: "AND",
        },
      }),

      // 7a. Planning kickoff Asana API (True — premium)
      n("8", "hitApi", "Create Webinar Planning Project in Asana", 1720, {
        hitApiDetails: {
          method: "POST",
          url: "https://app.asana.com/api/1.0/tasks",
          headers: [
            { key: "Authorization", value: "Bearer {asana_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "data",
              value:
                '{"name":"Webinar: {webinar_1_title} with {partner_company}","notes":"Partner: {speaker_name} ({speaker_title})\\nAuthority: {authority_level} ({authority_score}/100)\\nEst. Attendance: {webinar_1_attendance}\\nFormat: {recommended_format} ({optimal_duration})\\nPromotion: {promotion_plan}\\nLogistics: {logistics_summary}","projects":["{asana_webinar_project_id}"]}',
            },
          ],
          variables: [
            { name: "data.gid", customName: "asana_task_id", type: "string" },
            {
              name: "data.name",
              customName: "asana_task_name",
              type: "string",
            },
          ],
        },
      }),

      // 7b. ROI tracking Sheets (False — standard)
      sheetsNode(
        "9",
        "Track Webinar Partnership in Sheets",
        1720,
        "WebinarPartners",
        `[["${new Date().toISOString().split("T")[0]}", "{partner_company}", "{speaker_name}", "{speaker_email}", "{authority_score}", "{authority_level}", "{partnership_tier}", "{webinar_1_title}", "{webinar_1_attendance}", "{combined_audience}", "{topic_relevance}", "proposal_sent"]]`,
        400,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
      e("7", "9", "output_2"),
    ],
  },

  // ===================================================================
  // 1744. COMMUNITY MODERATOR OUTREACH (7 nodes)
  // ===================================================================

  "Community Moderator Outreach": {
    summary:
      "Fires when a community moderator is identified via webhook, researches their community involvement with runCode, AI-generates compliant outreach content, sends the outreach email, waits 14 days for response monitoring, plans community engagement activities with runCode, and tracks the relationship in Sheets.",
    nodes: [
      trigger(
        "Webhook: Community moderator identified for outreach",
        "webhook_app",
        {
          source: "community_ops",
          event: "moderator.identified",
          howItStarts:
            "Fires when the community team identifies an influential moderator in a relevant online community (Reddit, Discord, Slack, forums) who could be a valuable advocate or partnership candidate",
          setup:
            "Configure internal webhook for moderator identification events. Set OpenAI API key for compliant outreach generation. Configure email credentials. Set Google Sheets for relationship tracking.",
          inputFields: [],
        },
      ),

      // 2. AI extract moderator data
      aiNode(
        "2",
        "AI Extract Moderator Profile Data",
        280,
        "You are a community data parser. Extract the moderator name, email, community platform, community name, community size, moderator role, content topics, engagement frequency, notable posts, influence level, and the outreach reason from the raw webhook payload. Respond JSON: {moderator_name, moderator_email, platform, community_name, community_size, moderator_role, content_topics, engagement_frequency, notable_posts, influence_level, outreach_reason}",
        "Raw webhook payload: {webhook_payload}. Extract all moderator data regardless of field naming conventions.",
        [
          { field: "moderator_name", as: "mod_name", type: "string" },
          { field: "moderator_email", as: "mod_email", type: "string" },
          { field: "platform", as: "platform", type: "string" },
          { field: "community_name", as: "community_name", type: "string" },
          { field: "community_size", as: "community_size", type: "string" },
          { field: "moderator_role", as: "mod_role", type: "string" },
          { field: "content_topics", as: "content_topics", type: "string" },
          {
            field: "engagement_frequency",
            as: "engagement_freq",
            type: "string",
          },
          { field: "notable_posts", as: "notable_posts", type: "string" },
          { field: "influence_level", as: "influence_level", type: "string" },
          { field: "outreach_reason", as: "outreach_reason", type: "string" },
        ],
      ),

      // 3. Moderator community research runCode
      n("3", "runCode", "Research Moderator Community Context", 520, {
        runCodeDetails: {
          code: `const communitySize = parseInt(variables.community_size) || 0;
const influence = (variables.influence_level || '').toLowerCase();
const platform = (variables.platform || '').toLowerCase();
let outreachTone = 'casual';
if (platform === 'linkedin' || platform === 'slack') outreachTone = 'professional';
else if (platform === 'reddit' || platform === 'discord') outreachTone = 'casual';
else if (platform === 'github') outreachTone = 'technical';
const communityValue = communitySize > 10000 ? 'high' : communitySize > 1000 ? 'medium' : 'niche';
const complianceNotes = 'Do NOT mention products directly. Focus on community value. Disclose any commercial relationship. Follow ' + platform + ' community guidelines. No spam or self-promotion.';
const approachType = influence === 'high' ? 'thought_leadership_collaboration' : influence === 'medium' ? 'content_partnership' : 'community_contribution';
output.outreach_tone = outreachTone;
output.community_value = communityValue;
output.compliance_notes = complianceNotes;
output.approach_type = approachType;
output.platform_specific_tips = platform === 'reddit' ? 'Reference specific subreddit rules. Never DM unsolicited.' : platform === 'discord' ? 'Engage in server first. Build rapport before DMs.' : 'Engage with their content before outreach.';`,
          outputVariables: [
            { name: "outreach_tone", type: "string" },
            { name: "community_value", type: "string" },
            { name: "compliance_notes", type: "string" },
            { name: "approach_type", type: "string" },
            { name: "platform_specific_tips", type: "string" },
          ],
        },
      }),

      // 4. AI compliant outreach generation
      aiNode(
        "4",
        "AI Generate Compliant Community Outreach",
        760,
        "You are a community relations expert. Craft a genuine, non-promotional outreach message to a community moderator. The tone should match the platform culture. Lead with genuine appreciation for their community work, offer value first (guest content, expertise, resources), and avoid any direct product pitches. Return JSON: {outreach_subject, outreach_body, value_offer, appreciation_note, compliance_check, follow_up_plan}",
        "Moderator: {mod_name} ({mod_role}) | Platform: {platform} | Community: {community_name} ({community_size} members) | Topics: {content_topics} | Notable: {notable_posts} | Tone: {outreach_tone} | Approach: {approach_type} | Compliance: {compliance_notes} | Tips: {platform_specific_tips} | Reason: {outreach_reason}",
        [
          { field: "outreach_subject", as: "outreach_subject", type: "string" },
          { field: "outreach_body", as: "outreach_body", type: "string" },
          { field: "value_offer", as: "value_offer", type: "string" },
          {
            field: "appreciation_note",
            as: "appreciation_note",
            type: "string",
          },
          { field: "compliance_check", as: "compliance_check", type: "string" },
          { field: "follow_up_plan", as: "follow_up_plan", type: "string" },
        ],
      ),

      // 5. Outreach email send
      emailNode(
        "5",
        "Send Moderator Outreach Email",
        1000,
        "{mod_email}",
        "{outreach_subject}",
        "{outreach_body}",
      ),

      // 6. Response monitoring delay 14 days
      n("6", "delayAction", "Wait 14 Days for Response Monitoring", 1240, {
        delayDetails: { duration: 14, unit: "days" },
      }),

      // 7. Relationship tracking Sheets
      sheetsNode(
        "7",
        "Track Moderator Relationship in Sheets",
        1480,
        "ModeratorOutreach",
        `[["${new Date().toISOString().split("T")[0]}", "{mod_name}", "{mod_email}", "{platform}", "{community_name}", "{community_size}", "{influence_level}", "{approach_type}", "{community_value}", "{value_offer}", "{compliance_check}", "outreach_sent"]]`,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1745. COMMUNITY MODERATOR RECRUITING (8 nodes)
  // ===================================================================

  "Community Moderator Recruiting": {
    summary:
      "Runs on a schedule to score community engagement with runCode, enriches backgrounds via Clearbit API, AI-generates recruitment pitches, sends recruitment outreach emails, branches on interest tracking condition, runs onboarding automation with runCode, and monitors performance retention in Sheets.",
    nodes: [
      trigger(
        "Schedule: Weekly community moderator recruitment scan",
        "schedule",
        {
          schedule: "Every Friday at 10:00 AM",
          howItStarts:
            "Runs weekly to identify and recruit new community moderators based on engagement scores, contribution quality, and community leadership potential from the existing member base",
          setup:
            "Configure community platform API for engagement data. Set Clearbit API key for background enrichment. Set OpenAI API key for recruitment pitch generation. Configure email credentials. Set Google Sheets for performance tracking.",
          inputFields: [],
        },
      ),

      // 2. Engagement scoring runCode
      n("2", "runCode", "Score Community Member Engagement", 280, {
        runCodeDetails: {
          code: `// Simulate scoring from community platform data
const members = [
  { name: 'Alex Chen', email: 'alex.chen@techcorp.com', posts: 45, replies: 120, helpful_votes: 89, days_active: 180, reported_content: 0 },
];
const m = members[0];
let score = 0;
score += Math.min(30, m.posts * 0.7);
score += Math.min(25, m.replies * 0.2);
score += Math.min(25, m.helpful_votes * 0.3);
score += Math.min(15, m.days_active * 0.08);
score -= m.reported_content * 10;
const qualified = score >= 50;
output.candidate_name = m.name;
output.candidate_email = m.email;
output.engagement_score = Math.round(Math.min(100, score));
output.post_count = m.posts;
output.reply_count = m.replies;
output.helpful_votes = m.helpful_votes;
output.days_active = m.days_active;
output.is_qualified = qualified ? 'yes' : 'no';
output.mod_readiness = score >= 75 ? 'ready' : score >= 50 ? 'promising' : 'not_ready';`,
          outputVariables: [
            { name: "candidate_name", type: "string" },
            { name: "candidate_email", type: "string" },
            { name: "engagement_score", type: "number" },
            { name: "post_count", type: "number" },
            { name: "reply_count", type: "number" },
            { name: "helpful_votes", type: "number" },
            { name: "days_active", type: "number" },
            { name: "is_qualified", type: "string" },
            { name: "mod_readiness", type: "string" },
          ],
        },
      }),

      // 3. Background enrichment Clearbit API
      n("3", "hitApi", "Enrich Candidate Background via Clearbit", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://person.clearbit.com/v2/people/find?email={candidate_email}",
          headers: [
            { key: "Authorization", value: "Bearer {clearbit_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "name.fullName",
              customName: "enriched_name",
              type: "string",
            },
            { name: "title", customName: "enriched_title", type: "string" },
            {
              name: "company.name",
              customName: "enriched_company",
              type: "string",
            },
            { name: "bio", customName: "candidate_bio", type: "string" },
            {
              name: "linkedin.handle",
              customName: "linkedin_handle",
              type: "string",
            },
          ],
        },
      }),

      // 4. AI recruitment pitch
      aiNode(
        "4",
        "AI Generate Moderator Recruitment Pitch",
        760,
        "You are a community management expert. Craft a warm and flattering recruitment email inviting a top community member to become a moderator. Highlight their specific contributions, explain the benefits (title, influence, early access, swag, networking), and outline the light commitment. Return JSON: {recruitment_subject, recruitment_body, highlighted_contributions, moderator_benefits, time_commitment, onboarding_preview}",
        "Candidate: {candidate_name} ({enriched_title} at {enriched_company}) | Engagement Score: {engagement_score}/100 | Posts: {post_count} | Replies: {reply_count} | Helpful Votes: {helpful_votes} | Active Days: {days_active} | Readiness: {mod_readiness} | Bio: {candidate_bio}",
        [
          {
            field: "recruitment_subject",
            as: "recruit_subject",
            type: "string",
          },
          { field: "recruitment_body", as: "recruit_body", type: "string" },
          {
            field: "highlighted_contributions",
            as: "highlighted_contributions",
            type: "string",
          },
          { field: "moderator_benefits", as: "mod_benefits", type: "string" },
          { field: "time_commitment", as: "time_commitment", type: "string" },
          {
            field: "onboarding_preview",
            as: "onboarding_preview",
            type: "string",
          },
        ],
      ),

      // 5. Recruitment email outreach
      emailNode(
        "5",
        "Send Moderator Recruitment Email",
        1000,
        "{candidate_email}",
        "{recruit_subject}",
        "{recruit_body}",
      ),

      // 6. Interest tracking condition
      n(
        "6",
        "condition",
        "Check if Candidate is Ready for Immediate Onboarding",
        1240,
        {
          conditionDetails: {
            conditions: [
              { variable: "mod_readiness", operator: "equals", value: "ready" },
            ],
            logicalOperator: "AND",
          },
        },
      ),

      // 7a. Moderator onboarding runCode (True — ready)
      n("7", "runCode", "Prepare Moderator Onboarding Package", 1480, {
        runCodeDetails: {
          code: `const name = variables.candidate_name || 'New Moderator';
const score = variables.engagement_score || 0;
output.welcome_message = 'Welcome aboard, ' + name + '! Your engagement score of ' + score + '/100 made you a standout candidate.';
output.onboarding_checklist = '1. Review Community Guidelines\\n2. Complete Moderator Training Module\\n3. Shadow existing moderator for 1 week\\n4. Set up moderator tools and permissions\\n5. First solo moderation shift';
output.permissions_to_grant = 'content_moderation, user_warnings, pin_posts, move_threads, edit_tags';
output.mentor_assignment = 'Assign senior moderator as onboarding buddy';
output.first_shift_date = new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
output.onboarding_status = 'package_prepared';`,
          outputVariables: [
            { name: "welcome_message", type: "string" },
            { name: "onboarding_checklist", type: "string" },
            { name: "permissions_to_grant", type: "string" },
            { name: "mentor_assignment", type: "string" },
            { name: "first_shift_date", type: "string" },
            { name: "onboarding_status", type: "string" },
          ],
        },
      }),

      // 7b. Performance retention monitoring Sheets (False — promising, needs nurturing)
      sheetsNode(
        "8",
        "Track Moderator Recruitment in Sheets",
        1480,
        "ModeratorRecruitment",
        `[["${new Date().toISOString().split("T")[0]}", "{candidate_name}", "{candidate_email}", "{enriched_title}", "{enriched_company}", "{engagement_score}", "{mod_readiness}", "{post_count}", "{reply_count}", "{helpful_votes}", "{days_active}", "{highlighted_contributions}", "recruitment_sent"]]`,
        400,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("6", "8", "output_2"),
    ],
  },

  // ===================================================================
  // 1746. COMPANIES RUNNING PAID SEARCH (8 nodes)
  // ===================================================================

  "Companies Running Paid Search": {
    summary:
      "Runs on a schedule to identify companies running paid search via enrichment API, AI-analyzes their ad strategy, branches on ICP matching condition, AI-generates optimization outreach, discovers contacts via API, sends outreach emails, and creates CRM pipeline entries in HubSpot.",
    nodes: [
      trigger("Schedule: Weekly paid search advertiser scan", "schedule", {
        schedule: "Every Tuesday at 8:00 AM",
        howItStarts:
          "Runs weekly to identify companies actively running paid search campaigns (Google Ads, Bing Ads) in target verticals, indicating marketing budget availability and digital maturity",
        setup:
          "Configure ad intelligence API (SpyFu or SEMrush). Set OpenAI API key for strategy analysis and outreach generation. Configure contact discovery API. Set email credentials. Configure HubSpot token for pipeline creation.",
        inputFields: [],
      }),

      // 2. Advertiser identification enrichment API
      n("2", "hitApi", "Identify Active Paid Search Advertisers", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.spyfu.com/v1/organic-competitors?domain={target_vertical_domain}&country=US",
          headers: [
            { key: "Authorization", value: "Bearer {spyfu_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "results[0].domain",
              customName: "advertiser_domain",
              type: "string",
            },
            {
              name: "results[0].monthly_budget",
              customName: "monthly_ad_budget",
              type: "string",
            },
            {
              name: "results[0].keywords_count",
              customName: "keyword_count",
              type: "string",
            },
            {
              name: "results[0].ad_copy_count",
              customName: "ad_copy_count",
              type: "string",
            },
            {
              name: "results[0].top_keywords",
              customName: "top_keywords",
              type: "string",
            },
            {
              name: "results[0].industry",
              customName: "advertiser_industry",
              type: "string",
            },
            { name: "total", customName: "total_advertisers", type: "number" },
          ],
        },
      }),

      // 3. AI ad strategy analysis
      aiNode(
        "3",
        "AI Analyze Paid Search Strategy",
        520,
        "You are a paid search marketing analyst. Based on the advertiser data, analyze their paid search strategy strengths and weaknesses. Identify optimization opportunities, wasted spend indicators, and competitive gaps. Return JSON: {strategy_assessment, budget_efficiency, keyword_gaps, ad_copy_quality, landing_page_issues, optimization_opportunities, estimated_wasted_spend, competitive_advantage, improvement_potential_pct}",
        "Advertiser: {advertiser_domain} | Industry: {advertiser_industry} | Monthly Budget: ${monthly_ad_budget} | Keywords: {keyword_count} | Ad Copies: {ad_copy_count} | Top Keywords: {top_keywords}",
        [
          {
            field: "strategy_assessment",
            as: "strategy_assessment",
            type: "string",
          },
          {
            field: "budget_efficiency",
            as: "budget_efficiency",
            type: "string",
          },
          { field: "keyword_gaps", as: "keyword_gaps", type: "string" },
          {
            field: "optimization_opportunities",
            as: "optimization_opps",
            type: "string",
          },
          {
            field: "estimated_wasted_spend",
            as: "wasted_spend",
            type: "string",
          },
          {
            field: "competitive_advantage",
            as: "competitive_advantage",
            type: "string",
          },
          {
            field: "improvement_potential_pct",
            as: "improvement_potential",
            type: "string",
          },
        ],
      ),

      // 4. ICP matching prioritization condition
      n(
        "4",
        "condition",
        "Check if Advertiser Matches ICP Budget Threshold",
        760,
        {
          conditionDetails: {
            conditions: [
              {
                variable: "budget_efficiency",
                operator: "not_equals",
                value: "optimal",
              },
            ],
            logicalOperator: "AND",
          },
        },
      ),

      // 5a. AI optimization outreach (True — suboptimal efficiency, opportunity exists)
      aiNode(
        "5",
        "AI Generate Optimization Outreach",
        1000,
        "You are an expert PPC consulting outreach copywriter. Craft a compelling cold email offering paid search optimization insights. Lead with a specific observation about their current strategy, quantify the potential improvement, and offer a free audit. Return JSON: {outreach_subject, outreach_body, key_observation, value_quantification, free_offer, credibility_proof}",
        "Advertiser: {advertiser_domain} | Budget: ${monthly_ad_budget} | Assessment: {strategy_assessment} | Efficiency: {budget_efficiency} | Gaps: {keyword_gaps} | Opportunities: {optimization_opps} | Wasted Spend: {wasted_spend} | Improvement: {improvement_potential}%",
        [
          { field: "outreach_subject", as: "outreach_subject", type: "string" },
          { field: "outreach_body", as: "outreach_body", type: "string" },
          { field: "key_observation", as: "key_observation", type: "string" },
          {
            field: "value_quantification",
            as: "value_quantification",
            type: "string",
          },
          { field: "free_offer", as: "free_offer", type: "string" },
          {
            field: "credibility_proof",
            as: "credibility_proof",
            type: "string",
          },
        ],
      ),

      // 6. Contact discovery API
      n("6", "hitApi", "Discover Marketing Contact at Advertiser", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.apollo.io/v1/mixed_people/search",
          headers: [
            { key: "Content-Type", value: "application/json" },
            { key: "X-Api-Key", value: "{apollo_api_key}" },
          ],
          body: [
            { key: "organization_domains", value: "[{advertiser_domain}]" },
            {
              key: "person_titles",
              value:
                '["VP Marketing","Head of Marketing","Director of Marketing","CMO","Growth Marketing","Demand Gen","Digital Marketing Manager","PPC Manager"]',
            },
            { key: "per_page", value: "1" },
          ],
          variables: [
            {
              name: "people[0].name",
              customName: "contact_name",
              type: "string",
            },
            {
              name: "people[0].email",
              customName: "contact_email",
              type: "string",
            },
            {
              name: "people[0].title",
              customName: "contact_title",
              type: "string",
            },
            {
              name: "people[0].organization.name",
              customName: "company_name",
              type: "string",
            },
          ],
        },
      }),

      // 7. Outreach email send
      emailNode(
        "7",
        "Send Paid Search Optimization Outreach",
        1480,
        "{contact_email}",
        "{outreach_subject}",
        "{outreach_body}",
      ),

      // 8. CRM pipeline creation HubSpot
      hubspotPostNode(
        "8",
        "Create Pipeline Entry in HubSpot",
        1720,
        "crm/v3/objects/deals",
        [
          {
            key: "properties",
            value:
              '{"dealname":"Paid Search Optimization — {company_name}","dealstage":"appointmentscheduled","pipeline":"default","amount":"{monthly_ad_budget}","notes_last_updated":"Paid search prospect. Monthly budget: ${monthly_ad_budget}. Keywords: {keyword_count}. Efficiency: {budget_efficiency}. Improvement potential: {improvement_potential}%. Key observation: {key_observation}. Wasted spend: {wasted_spend}.","hs_lead_status":"OUTREACH_SENT"}',
          },
        ],
        [
          { name: "id", customName: "new_deal_id", type: "string" },
          {
            name: "properties.dealname",
            customName: "deal_name",
            type: "string",
          },
        ],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1747. COMPANIES RUNNING SHOPPING ADS (8 nodes)
  // ===================================================================

  "Companies Running Shopping Ads": {
    summary:
      "Runs on a schedule to identify merchants running shopping ads via Clearbit API enrichment, AI-analyzes their product feed strategy, benchmarks competitive performance with runCode, AI-generates tailored outreach, discovers and validates contacts via API, sends outreach emails, and tracks the CRM pipeline in Sheets.",
    nodes: [
      trigger("Schedule: Weekly shopping ads merchant scan", "schedule", {
        schedule: "Every Wednesday at 8:00 AM",
        howItStarts:
          "Runs weekly to identify e-commerce companies actively running Google Shopping, Facebook Dynamic Product Ads, or other shopping feed campaigns, indicating product feed management needs and marketing budget",
        setup:
          "Configure ad intelligence API for shopping campaign detection. Set Clearbit API key for merchant enrichment. Set OpenAI API key for strategy analysis and outreach. Configure contact discovery API. Set email credentials. Set Google Sheets for tracking.",
        inputFields: [],
      }),

      // 2. Merchant identification enrichment Clearbit API
      n("2", "hitApi", "Identify Shopping Ad Merchants via Clearbit", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://company.clearbit.com/v2/companies/find?domain={target_ecommerce_domain}",
          headers: [
            { key: "Authorization", value: "Bearer {clearbit_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "name", customName: "merchant_name", type: "string" },
            {
              name: "category.industry",
              customName: "merchant_industry",
              type: "string",
            },
            {
              name: "metrics.employees",
              customName: "merchant_employees",
              type: "string",
            },
            {
              name: "metrics.annualRevenue",
              customName: "merchant_revenue",
              type: "string",
            },
            { name: "tech", customName: "merchant_tech_stack", type: "string" },
            {
              name: "geo.country",
              customName: "merchant_country",
              type: "string",
            },
            { name: "url", customName: "merchant_url", type: "string" },
          ],
        },
      }),

      // 3. AI product feed strategy analysis
      aiNode(
        "3",
        "AI Analyze Product Feed and Shopping Strategy",
        520,
        "You are an e-commerce advertising analyst specializing in shopping feeds. Based on the merchant profile and tech stack, analyze their likely shopping ad strategy. Identify feed optimization opportunities, ROAS improvement areas, and competitive positioning gaps. Return JSON: {feed_health_assessment, roas_estimate, product_count_estimate, feed_optimization_opportunities, channel_coverage, competitive_gaps, improvement_areas, estimated_monthly_shopping_spend, feed_technology_used}",
        "Merchant: {merchant_name} | Industry: {merchant_industry} | Employees: {merchant_employees} | Revenue: {merchant_revenue} | Tech Stack: {merchant_tech_stack} | Country: {merchant_country} | URL: {merchant_url}",
        [
          {
            field: "feed_health_assessment",
            as: "feed_health",
            type: "string",
          },
          { field: "roas_estimate", as: "roas_estimate", type: "string" },
          {
            field: "product_count_estimate",
            as: "product_count",
            type: "string",
          },
          {
            field: "feed_optimization_opportunities",
            as: "feed_opps",
            type: "string",
          },
          { field: "channel_coverage", as: "channel_coverage", type: "string" },
          { field: "competitive_gaps", as: "competitive_gaps", type: "string" },
          {
            field: "improvement_areas",
            as: "improvement_areas",
            type: "string",
          },
          {
            field: "estimated_monthly_shopping_spend",
            as: "monthly_spend",
            type: "string",
          },
        ],
      ),

      // 4. Competitive benchmarking runCode
      n(
        "4",
        "runCode",
        "Benchmark Shopping Performance Against Industry",
        760,
        {
          runCodeDetails: {
            code: `const roas = parseFloat(variables.roas_estimate) || 0;
const employees = parseInt(variables.merchant_employees) || 0;
const revenue = parseInt(variables.merchant_revenue) || 0;
const industryBenchmarks = {
  avg_roas: 4.5,
  avg_cpc: 0.45,
  avg_conversion_rate: 2.8,
  top_quartile_roas: 7.2,
};
const roasGap = industryBenchmarks.avg_roas - roas;
const aboveBenchmark = roas >= industryBenchmarks.avg_roas;
const topQuartileGap = industryBenchmarks.top_quartile_roas - roas;
let merchantTier = 'emerging';
if (revenue > 50000000) merchantTier = 'enterprise';
else if (revenue > 10000000) merchantTier = 'mid_market';
else if (revenue > 1000000) merchantTier = 'growth';
const outreachPriority = !aboveBenchmark && revenue > 1000000 ? 'high' : !aboveBenchmark ? 'medium' : 'low';
output.roas_gap = roasGap.toFixed(1);
output.above_benchmark = aboveBenchmark ? 'yes' : 'no';
output.top_quartile_gap = topQuartileGap.toFixed(1);
output.merchant_tier = merchantTier;
output.outreach_priority = outreachPriority;
output.benchmark_summary = 'Current ROAS: ' + roas + ' vs Industry Avg: ' + industryBenchmarks.avg_roas + ' (Gap: ' + roasGap.toFixed(1) + '). Top quartile: ' + industryBenchmarks.top_quartile_roas;
output.improvement_value = Math.round(roasGap * parseFloat(variables.monthly_spend || '0') * 0.1);`,
            outputVariables: [
              { name: "roas_gap", type: "string" },
              { name: "above_benchmark", type: "string" },
              { name: "top_quartile_gap", type: "string" },
              { name: "merchant_tier", type: "string" },
              { name: "outreach_priority", type: "string" },
              { name: "benchmark_summary", type: "string" },
              { name: "improvement_value", type: "number" },
            ],
          },
        },
      ),

      // 5. AI tailored outreach generation
      aiNode(
        "5",
        "AI Generate Tailored Shopping Ads Outreach",
        1000,
        "You are an expert e-commerce advertising copywriter. Craft a compelling outreach email for a merchant running shopping ads. Lead with a specific benchmark insight, quantify the improvement opportunity, and offer a free feed audit. Be data-driven and specific. Return JSON: {outreach_subject, outreach_body, benchmark_hook, improvement_opportunity, free_audit_offer, social_proof}",
        "Merchant: {merchant_name} ({merchant_tier}) | Industry: {merchant_industry} | Feed Health: {feed_health} | ROAS: {roas_estimate} | Benchmark: {benchmark_summary} | Gaps: {competitive_gaps} | Improvement Areas: {improvement_areas} | Feed Opps: {feed_opps} | Monthly Spend: {monthly_spend} | Potential Value: ${improvement_value}",
        [
          { field: "outreach_subject", as: "shopping_subject", type: "string" },
          { field: "outreach_body", as: "shopping_body", type: "string" },
          { field: "benchmark_hook", as: "benchmark_hook", type: "string" },
          {
            field: "improvement_opportunity",
            as: "improvement_opp",
            type: "string",
          },
          { field: "free_audit_offer", as: "audit_offer", type: "string" },
          { field: "social_proof", as: "social_proof", type: "string" },
        ],
      ),

      // 6. Contact discovery validation API
      n("6", "hitApi", "Discover E-Commerce Marketing Contact", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.apollo.io/v1/mixed_people/search",
          headers: [
            { key: "Content-Type", value: "application/json" },
            { key: "X-Api-Key", value: "{apollo_api_key}" },
          ],
          body: [
            { key: "organization_domains", value: "[{merchant_url}]" },
            {
              key: "person_titles",
              value:
                '["E-Commerce Manager","Head of E-Commerce","Director of E-Commerce","VP Marketing","Digital Marketing Director","Performance Marketing","Marketplace Manager","Shopping Feed Manager"]',
            },
            { key: "per_page", value: "1" },
          ],
          variables: [
            {
              name: "people[0].name",
              customName: "contact_name",
              type: "string",
            },
            {
              name: "people[0].email",
              customName: "contact_email",
              type: "string",
            },
            {
              name: "people[0].title",
              customName: "contact_title",
              type: "string",
            },
            {
              name: "people[0].organization.name",
              customName: "org_name",
              type: "string",
            },
          ],
        },
      }),

      // 7. Outreach email send
      emailNode(
        "7",
        "Send Shopping Ads Outreach Email",
        1480,
        "{contact_email}",
        "{shopping_subject}",
        "{shopping_body}",
      ),

      // 8. CRM tracking Sheets
      sheetsNode(
        "8",
        "Track Shopping Ads Prospect in Pipeline",
        1720,
        "ShoppingAdsPipeline",
        `[["${new Date().toISOString().split("T")[0]}", "{merchant_name}", "{contact_name}", "{contact_email}", "{merchant_industry}", "{merchant_tier}", "{roas_estimate}", "{roas_gap}", "{monthly_spend}", "{improvement_value}", "{feed_health}", "{outreach_priority}", "{competitive_gaps}", "outreach_sent"]]`,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },
};
